import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ArrowRight, CheckCircle, Sparkles, Sun, Moon, ChevronDown, ChevronLeft, ChevronRight, X, Target, Trophy, Clock, BookOpen, Play, Users, Zap, Search, GraduationCap, TrendingUp, Award, Mail, Phone, MapPin, Send, Star, Shield, Coins, Globe, Lock, Plus, Menu as MenuIcon, LogOut, Info, Rocket, Plane, Swords, Keyboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useGoogleOneTapLogin } from '@react-oauth/google';
import { API } from '../App';
import { queryClient, apiGet } from '../lib/queryClient';
import { prefetchRouteForPath } from '../lib/routePrefetch';
import { useSiteContent } from '../contexts/SiteContentContext';
import { useSiteTexts } from '../context/SiteTextsContext';
import { useCMSVisibility } from '../context/CMSVisibilityContext';
import { LandingPageSkeleton } from '../components/Skeletons';
import { useCredits } from '../context/CreditContext';
import { useLanguage, SUPPORTED_LANGUAGES } from '../context/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { TopicThumbnailCard } from '../components/TopicThumbnail';
import LiveGameCard from '../components/LiveGameCard';
import QuickQuizPlayer from '../components/QuickQuizPlayer';
import { useSetNavbarConfig } from '../context/NavbarContext';
import { useAuthModal } from '../context/AuthModalContext';
import { useTheme } from '../context/ThemeContext';
import { useGamesEnabled } from '../hooks/useGamesEnabled';

// Icon map for dynamic icon rendering
const IconMap = {
  Target, Trophy, Clock, BookOpen, Users, Zap, TrendingUp, Award, Star, Shield
};

const getIcon = (iconName) => IconMap[iconName] || Target;

// Virtualized slot: renders children only when slot is near the viewport.
// When out of view, the slot keeps its layout slot (fixed minHeight) with no children
// so the page DOM stays light without breaking scroll position.
function VirtualSlot({ minHeight = 150, rootMargin = '800px 0px', children, testId }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(true); // start visible to avoid initial flicker
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return (
    <div ref={ref} style={{ minHeight, contain: 'layout' }} data-testid={testId} data-virtual-state={inView ? 'in' : 'out'}>
      {inView ? children : null}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  // While we're still fetching the topic catalogue, render a row of
  // pulsing skeleton cards instead of "no results" so the page doesn't
  // appear empty during the round-trip.
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  // Theme is owned by ThemeContext (ThemeProvider in App.js). Previously
  // this page kept its own local `isDark` state seeded from localStorage,
  // which meant toggling the theme from the navbar only re-rendered the
  // navbar — Landing's local copy stayed stale until a full reload. Using
  // the context here makes every `isDark` reference (and every component
  // we pass it down to as a prop) re-render in lock-step with the toggle.
  const { isDark } = useTheme();
  // Auth modal is now global (mounted in MainLayout). We keep the local
  // `showAuth`/`setShowAuth` API as a thin alias so the dozens of click
  // handlers across this page can keep calling `setShowAuth(true)`
  // unchanged.
  const { isOpen: showAuth, openAuth, closeAuth } = useAuthModal();
  const setShowAuth = (v) => (v ? openAuth() : closeAuth());
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyCertId, setVerifyCertId] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedTopicSubtopics, setSelectedTopicSubtopics] = useState([]);
  const [comingSoonTapped, setComingSoonTapped] = useState(null);
  // Topic currently shown in the info side-sheet (triggered by the `i`
  // icon on each topic card). null = sheet closed.
  const [infoTopic, setInfoTopic] = useState(null);
  // Game-mode instructions sheet — opened from the `i` icon on a game card.
  const [gameInfoMode, setGameInfoMode] = useState(null);

  // Store intended redirect when opening auth modal
  const openAuthWithRedirect = (path) => {
    if (path) localStorage.setItem('ikz_pending_redirect', path);
    openAuth();
  };
  const [loadingSubtopics, setLoadingSubtopics] = useState(false);
  const [guestTestStarted, setGuestTestStarted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const [liveSessions, setLiveSessions] = useState([]);
  // Tab state — persisted to URL hash (e.g. #games, #games/tug-of-war, #quick-quiz, #courses)
  const parseHash = () => {
    const h = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
    if (!h) return { section: 'courses', mode: null };
    const [section, mode] = h.split('/');
    if (['courses', 'quick-quiz', 'live-games'].includes(section)) return { section, mode: mode || null };
    if (section === 'games') return { section: 'live-games', mode: mode || null };
    return { section: 'courses', mode: null };
  };
  const initialHash = parseHash();
  const [activeSection, setActiveSection] = useState(initialHash.section);
  const [activeGameMode, setActiveGameMode] = useState(initialHash.mode); // 'tug-of-war' | 'knowledge-climb' | 'speed-sprint' | 'rocket-race' | null
  // Layered Games-tab navigation: mode → category → play type
  const [gameCategory, setGameCategory] = useState(null); // 'education' | 'driving' | ... | 'all' | null
  const [gamePlayType, setGamePlayType] = useState(null); // 'solo' | 'bot' | 'others' | null
  const [liveSessionsForCat, setLiveSessionsForCat] = useState(null); // [] when fetched, null while loading
  const [gameCategoriesData, setGameCategoriesData] = useState(null); // [{id, count}] from backend; null = loading

  // Solo never needs a "Ready to play" intro — it should jump straight
  // into the game's countdown. For Bot / Others we still let the user
  // confirm or pick a live lobby. Single-mode solo games (Knowledge climb)
  // get the same treatment: pick category → launch.
  const SINGLE_SOLO_MODES = { 'knowledge-climb': true };
  // Per-game on/off map controlled by admins via /admin/games. Hidden games
  // simply disappear from this Games tab.
  const { isEnabled: isGameEnabled, sortByOrder: sortGamesByOrder } = useGamesEnabled();
  const launchGamePool = useCallback((play) => {
    if (!activeGameMode || !gameCategory) return;
    navigate(`/games/${activeGameMode}/pool?cat=${gameCategory}&mode=${activeGameMode}&play=${play}&count=10`);
  }, [activeGameMode, gameCategory, navigate]);

  useEffect(() => {
    if (!activeGameMode || !gameCategory || gamePlayType) return;
    if (SINGLE_SOLO_MODES[activeGameMode]) launchGamePool('solo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGameMode, gameCategory, gamePlayType, launchGamePool]);

  const pickGamePlayType = (p) => {
    if (p === 'solo') { launchGamePool('solo'); return; }
    setGamePlayType(p);
  };
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Course search — opened by the search icon next to the category pills.
  // Filters topics by name + description + category.
  const [topicSearch, setTopicSearch] = useState('');
  const [topicSearchOpen, setTopicSearchOpen] = useState(false);
  // Horizontal scroll ref for the topic cards row (large-screen carousel).
  const topicsScrollerRef = useRef(null);
  const [topicScroll, setTopicScroll] = useState({ canLeft: false, canRight: false });

  // Track whether the topics row can scroll further left / right so we
  // can enable/disable the nav arrows.
  const updateTopicScrollState = useCallback(() => {
    const el = topicsScrollerRef.current;
    if (!el) return;
    const canLeft = el.scrollLeft > 4;
    const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setTopicScroll({ canLeft, canRight });
  }, []);

  const scrollTopicsBy = useCallback((direction) => {
    const el = topicsScrollerRef.current;
    if (!el) return;
    const step = Math.max(240, Math.round(el.clientWidth * 0.6));
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  // Whenever the topics list changes, recompute the scroll state so the
  // arrow disabled state reflects the new content width.
  useEffect(() => {
    const id = window.setTimeout(updateTopicScrollState, 50);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, topicSearch]);
  const [quickQuizCategory, setQuickQuizCategory] = useState('all');
  // Quick-quiz language filter for the home page. Defaults to the user's
  // header language preference so visitors see content in their selected
  // language first; they can switch to "all" to broaden the grid.
  const { language: headerLanguage } = useLanguage();
  const [quickQuizLanguage, setQuickQuizLanguage] = useState(headerLanguage || 'en');
  // Keep filter in sync if the user changes the header language while on
  // the page — but only if they haven't manually overridden to "all" or
  // another language since arriving.
  const langSyncRef = useRef(true);
  useEffect(() => {
    if (langSyncRef.current) setQuickQuizLanguage(headerLanguage || 'en');
  }, [headerLanguage]);
  const [quickQuizSearch, setQuickQuizSearch] = useState('');
  const [quickQuizzes, setQuickQuizzes] = useState([]);
  const [extraQuizzes, setExtraQuizzes] = useState([]);     // appended pages from /quizzes/browse
  const [extraPage, setExtraPage] = useState(0);            // last page fetched (0 = none)
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreQuizzes, setHasMoreQuizzes] = useState(true);
  const [topLearners, setTopLearners] = useState([]);
  const [activeQuickQuiz, setActiveQuickQuiz] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [quizLeaderboard, setQuizLeaderboard] = useState(null); // {slug, leaders[]}
  const sectionCardRef = useRef(null);
  const [tabProgress, setTabProgress] = useState(0); // 0..1, fills underline while tabs are stuck
  const [heroStats, setHeroStats] = useState({ active_learners_week: null, quizzes_published: null, live_games_now: null });

  // Track taken quizzes in localStorage
  const getTakenQuizzes = () => {
    try { return JSON.parse(localStorage.getItem('ikz_taken_quizzes') || '{}'); } catch { return {}; }
  };
  const markQuizTaken = (slug, name, score) => {
    const taken = getTakenQuizzes();
    taken[slug] = { name, score, at: Date.now() };
    localStorage.setItem('ikz_taken_quizzes', JSON.stringify(taken));
  };

  // Visitor ID for likes
  const getVisitorId = () => {
    let vid = localStorage.getItem('ikz_visitor_id');
    if (!vid) { vid = 'v_' + Math.random().toString(36).substring(2, 12); localStorage.setItem('ikz_visitor_id', vid); }
    return vid;
  };

  // Liked quizzes in localStorage
  const getLikedQuizzes = () => {
    try { return JSON.parse(localStorage.getItem('ikz_liked_quizzes') || '{}'); } catch { return {}; }
  };
  const [likedQuizzes, setLikedQuizzes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ikz_liked_quizzes') || '{}'); } catch { return {}; }
  });
  const [quizStats, setQuizStats] = useState({}); // {slug: {views, likes}}

  const toggleLike = async (slug, e) => {
    e.stopPropagation();
    const vid = getVisitorId();
    try {
      const res = await axios.post(`${API}/quizzes/${slug}/like`, { visitor_id: vid });
      const newLiked = { ...likedQuizzes };
      if (res.data.liked) newLiked[slug] = true;
      else delete newLiked[slug];
      setLikedQuizzes(newLiked);
      localStorage.setItem('ikz_liked_quizzes', JSON.stringify(newLiked));
      // Use server-returned count as source of truth (clamped at 0 server-side)
      if (typeof res.data.likes === 'number') {
        setQuizStats(s => ({ ...s, [slug]: { ...s[slug], likes: res.data.likes } }));
      }
    } catch {}
  };

  const trackView = (slug) => {
    axios.post(`${API}/quizzes/${slug}/view`).catch(() => {});
    setQuizStats(s => ({ ...s, [slug]: { ...s[slug], views: (s[slug]?.views || 0) + 1 } }));
  };

  const viewLeaderboard = async (slug) => {
    try {
      const res = await axios.get(`${API}/quizzes/${slug}/leaderboard`);
      setQuizLeaderboard({ slug, title: quickQuizzes.find(q => q.slug === slug)?.title || 'Quiz', leaders: res.data || [] });
    } catch { toast.error('Could not load leaderboard'); }
  };

  const loadMoreQuizzes = async () => {
    if (loadingMore || !hasMoreQuizzes) return;
    setLoadingMore(true);
    try {
      const next = extraPage + 1;
      // page=1 already shown via /landing/quick-quizzes — start from page 2 to avoid duplicates
      const res = await axios.get(`${API}/quizzes/browse?page=${next + 1}&limit=8`);
      const incoming = res.data.items || [];
      // Dedupe by slug against already-displayed items
      const existing = new Set([...quickQuizzes, ...extraQuizzes].map(q => q.slug).filter(Boolean));
      const fresh = incoming.filter(q => q.slug && !existing.has(q.slug));
      setExtraQuizzes(prev => [...prev, ...fresh]);
      setExtraPage(next);
      setHasMoreQuizzes(!!res.data.has_more);
    } catch { toast.error('Could not load more quizzes'); }
    finally { setLoadingMore(false); }
  };
  // Seed banners state from the react-query cache so return visits start at
  // the correct header offset (no visible shift as banners arrive async).
  const [landingBanners, setLandingBanners] = useState(
    () => queryClient.getQueryData(['/landing/banners']) || []
  );
  const [dismissedBanners, setDismissedBanners] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
    } catch {
      return [];
    }
  });
  
  // Use language context
  const { language, getTranslatedContent, isLanguageAvailable } = useLanguage();
  
  // Use site content from context
  const { content: siteContent, loading: contentLoading } = useSiteContent();
  // Schema-driven CMS strings — when the admin edits a key under
  // `Admin → Site Texts`, the value flows through here. Every call
  // takes a fallback so the UI never breaks before the admin saves a
  // value for a given key (or before the API responds).
  const { getText } = useSiteTexts();
  
  // Use CMS visibility context
  const { isLandingVisible, isGlobalVisible, isButtonVisible } = useCMSVisibility();

  // Use credit context for login callback
  const { onLogin, isAuthenticated, onLogout, wallet: creditWallet } = useCredits();
  // Hero "Subscribe" pill — shows the remaining subscription time when
  // active, otherwise a plain "Subscribe" CTA that opens /subscription.
  const heroSub = creditWallet?.subscription || null;
  const heroSubActive = !!(heroSub && heroSub.is_active);
  const heroSubRemainingLabel = (() => {
    if (!heroSubActive) return null;
    const d = heroSub.days_remaining || 0;
    const h = heroSub.hours_remaining || 0;
    const m = heroSub.minutes_remaining || 0;
    if (d > 0) return `${d}d ${h}h left`;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m left`;
    return `${m}m left`;
  })();
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  // Dismiss banner handler
  const dismissBanner = (bannerId) => {
    const newDismissed = [...dismissedBanners, bannerId];
    setDismissedBanners(newDismissed);
    localStorage.setItem('dismissedBanners', JSON.stringify(newDismissed));
  };

  // Get visible banners (not dismissed). Memoised so child memos that take
  // this array as a dep don't churn — `.filter()` builds a fresh array each
  // render, which would otherwise re-trigger `useSetNavbarConfig` in a loop.
  const visibleBanners = useMemo(
    () => landingBanners.filter(
      b => b.dismissible === false || !dismissedBanners.includes(b.announcement_id)
    ),
    [landingBanners, dismissedBanners]
  );

  // Languages - only EN, FR, RW (no flags)
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'rw', name: 'Kinyarwanda' }
  ];

  const [articles, setArticles] = useState([]);
  const [authChecked, setAuthChecked] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [pendingTopicId, setPendingTopicId] = useState(null);

  // Check if user is a returning user (has logged in before)
  const isReturningUser = useCallback(() => {
    return localStorage.getItem('ikizamini_returning_user') === 'true';
  }, []);

  // Mark user as returning after first login
  const markAsReturningUser = useCallback(() => {
    localStorage.setItem('ikizamini_returning_user', 'true');
  }, []);

  // Check auth - don't redirect, just track logged-in state
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setLoggedInUser(res.data);
        // Fetch resume data for logged-in user
        try {
          const enrolledRes = await axios.get(`${API}/user/enrolled-topics`, { withCredentials: true });
          const enrolled = enrolledRes.data?.enrolled || enrolledRes.data?.topics || [];
          if (enrolled.length > 0) {
            // Pick the most recently active topic
            const best = enrolled.sort((a, b) => (b.total_attempts || 0) - (a.total_attempts || 0))[0];
            setResumeData(best);
          }
        } catch {}
        setAuthChecked(true);
      } catch (error) {
        setAuthChecked(true);
      }
    };
    checkAuthStatus();
  }, []);

  // Auto Google Sign-in with One Tap
  const handleAutoGoogleLogin = useCallback(async (credentialResponse) => {
    try {
      const response = await axios.post(`${API}/auth/google`, {
        credential: credentialResponse.credential
      }, { 
        withCredentials: true,
        timeout: 15000
      });
      
      const userData = response.data;
      
      // Mark as returning user
      markAsReturningUser();
      
      toast.success(`Welcome back, ${userData.name}!`);
      
      // Check for pending redirects
      const pendingRedirect = localStorage.getItem('ikz_pending_redirect');
      const pendingTopic = localStorage.getItem('ikizamini_pending_topic');
      localStorage.removeItem('ikz_pending_redirect');
      localStorage.removeItem('ikizamini_pending_topic');
      if (pendingRedirect) {
        navigate(pendingRedirect);
      } else if (pendingTopic) {
        navigate(`/topic/${pendingTopic}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.log('Auto sign-in failed, user can sign in manually');
    }
  }, [navigate, markAsReturningUser]);

  // Google One Tap for automatic sign-in - only after auth check confirms user is not logged in
  useGoogleOneTapLogin({
    onSuccess: handleAutoGoogleLogin,
    onError: () => console.log('One Tap not available'),
    disabled: !authChecked || !!loggedInUser,
    auto_select: true,
    cancel_on_tap_outside: false,
    prompt_parent_id: 'oneTapContainer'
  });

  useEffect(() => {
    loadData();
    loadArticles();
    // Re-run loadData whenever the user signs in/out so the topic cards
    // pick up the fresh `started` / `completed_subtopics` values from
    // the now-authenticated /landing/topics response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Warm the JS chunks for routes users commonly visit next from the homepage.
  // Runs when the browser is idle so it never competes with the initial paint.
  useEffect(() => {
    const paths = ['/games', '/create', '/quizzes', '/leaderboard', '/credits'];
    const warm = () => paths.forEach(prefetchRouteForPath);
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warm, { timeout: 2500 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 1500);
    return () => clearTimeout(id);
  }, []);

  // ThemeContext owns the document.documentElement `dark` class + the
  // localStorage write — no need to duplicate it here.

  // Sync activeSection/activeGameMode → URL hash (replaceState so we don't pollute history)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let hash = '';
    if (activeSection === 'live-games') hash = activeGameMode ? `#games/${activeGameMode}` : '#games';
    else if (activeSection === 'quick-quiz') hash = '#quick-quiz';
    else if (activeSection === 'courses') hash = '#courses';
    if (window.location.hash !== hash) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
    }
  }, [activeSection, activeGameMode]);

  // Listen for hash changes (e.g. browser back/forward) and re-sync
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onHashChange = () => {
      const { section, mode } = parseHash();
      setActiveSection(section);
      setActiveGameMode(mode);
    };
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('popstate', onHashChange);
    };
  }, []);

  // If an admin has hidden the currently active landing tab from the CMS,
  // bounce the user to the first visible tab so the page never renders
  // an empty section. Skips when nothing's hidden (the common case).
  useEffect(() => {
    const tabMap = {
      'courses': 'tab_courses',
      'quick-quiz': 'tab_quick_quiz',
      'live-games': 'tab_games',
    };
    const currentKey = tabMap[activeSection];
    if (currentKey && !isLandingVisible(currentKey)) {
      const fallback = Object.keys(tabMap).find(t => isLandingVisible(tabMap[t]));
      if (fallback && fallback !== activeSection) setActiveSection(fallback);
    }
  }, [activeSection, isLandingVisible]);

  // Auto-open auth modal when /?signin=1 is hit (e.g., from "Sign in to take this test" toast)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('signin') === '1' && !isAuthenticated) {
      setShowAuth(true);
      // Clear the param so refresh doesn't keep popping it
      sp.delete('signin');
      const q = sp.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Compute progress (0..1) of the user scrolling through the active tab's content
  useEffect(() => {
    const compute = () => {
      const card = sectionCardRef.current;
      if (!card) return;
      const stickyOffset = visibleBanners.length > 0 ? 96 : 56;
      const tabsHeight = 80; // approx height of sticky tabs strip
      const rect = card.getBoundingClientRect();
      const scrollable = rect.height - tabsHeight; // distance the content can scroll past the tabs
      if (scrollable <= 0) { setTabProgress(0); return; }
      // amount scrolled past the top while tabs are stuck
      const scrolledPast = stickyOffset - rect.top;
      const p = Math.max(0, Math.min(1, scrolledPast / scrollable));
      setTabProgress(p);
    };
    compute();
    const onScroll = () => compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', compute);
    };
  }, [visibleBanners.length, activeSection]);

  const loadData = async () => {
    // Only show the full-screen skeleton if we have no cached data at all.
    // react-query's cache (60s staleTime) returns instantly on revisits,
    // so this becomes a true no-op on the second homepage visit.
    const cached = queryClient.getQueryData(['/landing/topics']);
    setDataLoading(!cached);
    // When the user has just signed in/out, invalidate the topics query
    // so we get a fresh response that reflects their progress (or lack
    // thereof). Other landing data (banners, leaderboard, hero stats)
    // is auth-agnostic and can keep its cache.
    queryClient.invalidateQueries({ queryKey: ['/landing/topics'] });
    try {
      const fetchCached = (path) =>
        queryClient.fetchQuery({ queryKey: [path], queryFn: apiGet }).catch(() => null);
      const [topicsRes, packagesRes, bannersRes, liveRes, quizzesRes, leaderRes, heroRes, gameCatsRes] = await Promise.all([
        fetchCached('/landing/topics'),
        fetchCached('/credit-packages'),
        fetchCached('/landing/banners'),
        fetchCached('/live-sessions/active'),
        fetchCached('/landing/quick-quizzes'),
        fetchCached('/landing/leaderboard'),
        fetchCached('/landing/hero-stats'),
        fetchCached('/games/categories'),
      ]);
      setTopics(topicsRes || []);
      setTopicsLoading(false);
      setPackages(packagesRes || []);
      setLandingBanners(bannersRes || []);
      // Backend already filters out finished/expired, but be defensive
      // in case the cached response carries stale data.
      const joinable = (liveRes || []).filter(s => {
        const st = s.status || 'lobby';
        return st !== 'finished' && st !== 'expired';
      });
      if (joinable.length > 0) {
        setLiveSession(joinable[0]);
        setLiveSessions(joinable);
      } else {
        setLiveSession(null);
        setLiveSessions([]);
      }
      setQuickQuizzes(quizzesRes || []);
      // Backend now returns a single merged array. Tolerate the legacy
      // {quiz, course} shape so cached responses don't crash.
      if (Array.isArray(leaderRes)) {
        setTopLearners(leaderRes);
      } else if (leaderRes && (leaderRes.quiz || leaderRes.course)) {
        setTopLearners([...(leaderRes.quiz || []), ...(leaderRes.course || [])]);
      } else {
        setTopLearners([]);
      }
      setHeroStats(heroRes || {});
      setGameCategoriesData((gameCatsRes && gameCatsRes.categories) || []);
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setDataLoading(false);
    }
  };

  const loadArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles/featured`);
      setArticles(response.data);
    } catch (error) {
      // Use default articles if API fails
      setArticles([
        { article_id: '1', title: 'How to Register for Driving License Test', description: 'Step-by-step guide to register for your driving license examination in Rwanda.', category: 'Guide', read_time: '5 min read' },
        { article_id: '2', title: 'Top 10 Tips to Pass Your Driving Test', description: 'Expert tips and strategies to help you ace your driving license test on the first try.', category: 'Tips', read_time: '4 min read' },
        { article_id: '3', title: 'Understanding Road Signs in Rwanda', description: 'Complete guide to all road signs you need to know for your driving test.', category: 'Education', read_time: '8 min read' }
      ]);
    }
  };

  // Fetch subtopics when a topic is selected
  const fetchTopicSubtopics = async (topicId) => {
    setLoadingSubtopics(true);
    try {
      const response = await axios.get(`${API}/topics/${topicId}/subtopics/public`);
      setSelectedTopicSubtopics(response.data || []);
    } catch (error) {
      console.error('Error fetching subtopics:', error);
      setSelectedTopicSubtopics([]);
    } finally {
      setLoadingSubtopics(false);
    }
  };

  // Handle topic selection - navigate directly for logged-in users
  const handleTopicSelect = (topic) => {
    // Topic detail is now public — go straight to the course.
    // Test attempts inside will prompt sign-in if needed.
    navigate(`/topic/${topic.topic_id}`);
  };

  const startGuestTest = async (topicId) => {
    // Check if guest has already taken a test
    const guestTests = JSON.parse(localStorage.getItem('guest_tests') || '[]');
    if (guestTests.length >= 1) {
      toast.info('Sign in to take more tests!');
      setShowAuth(true);
      return;
    }

    setGuestTestStarted(true);
    try {
      const response = await axios.post(`${API}/tests/guest-generate`, {
        topic_id: topicId,
        question_count: 5,  // Limited questions for guest
        language: language  // Pass the selected language
      });
      
      // Store guest test data
      const guestAttemptId = `guest_${Date.now()}`;
      sessionStorage.setItem(`test_${guestAttemptId}`, JSON.stringify({
        questions: response.data.questions,
        topic_name: response.data.topic_name,
        timer_mode: 'running',
        is_guest: true
      }));
      
      localStorage.setItem('guest_tests', JSON.stringify([...guestTests, guestAttemptId]));
      
      navigate(`/guest-test/${guestAttemptId}`);
    } catch (error) {
      toast.error('Failed to start test');
      setGuestTestStarted(false);
    }
  };

  const currentLangData = languages.find(l => l.code === language) || languages[0];

  // Filter topics based on search query
  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return topics;
    const query = searchQuery.toLowerCase();
    return topics.filter(topic => 
      topic.name.toLowerCase().includes(query) ||
      topic.description?.toLowerCase().includes(query)
    );
  }, [topics, searchQuery]);

  // Theme classes
  const bgMain = isDark ? 'bg-black' : 'bg-[#f5f7fa]';
  const textMain = isDark ? 'text-white' : 'text-gray-800';
  const textMuted = isDark ? 'text-gray-500' : 'text-gray-500';
  const textLight = isDark ? 'text-gray-400' : 'text-gray-600';
  const cardBg = isDark ? 'bg-gray-900/80' : 'bg-white/90';
  const borderColor = isDark ? 'border-gray-800' : 'border-gray-200';

  // Shared UnifiedNavbar (rendered in MainLayout): tell it to include the
  // Sign-In + Verify-Certificate options plus the dynamic banner stack
  // via `topSlot`. Memoize topSlot so the navbar config reference only
  // changes when banners actually change.
  const bannersSlot = useMemo(
    () => (
      <AnimatePresence initial={false}>
        {visibleBanners.length > 0 && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            {visibleBanners.map((banner) => {
              const bgColor = banner.type === 'warning' ? 'bg-yellow-500' : banner.type === 'alert' ? 'bg-red-500' : 'bg-brand-600';
              return (
                <div key={banner.announcement_id} className={`${bgColor} text-white`}>
                  <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
                    <p className="text-xs sm:text-sm flex-1">
                      {banner.title && <span className="font-medium">{banner.title}: </span>}
                      {banner.message}
                      {banner.link_url && (
                        <>
                          {' '}
                          <a href={banner.link_url} rel="noopener noreferrer" className="underline font-semibold hover:text-white/90">
                            {banner.link_text || banner.link_url}
                          </a>
                        </>
                      )}
                    </p>
                    {banner.dismissible !== false && (
                      <button onClick={() => dismissBanner(banner.announcement_id)} className="p-1.5 hover:bg-white/20 rounded transition-colors flex-shrink-0" aria-label="Dismiss banner">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    ),
    [visibleBanners]
  );

  const openSignIn = useCallback(() => setShowAuth(true), []);
  const openVerify = useCallback(() => setShowVerifyModal(true), []);
  useSetNavbarConfig({
    onSignInClick: openSignIn,
    onVerifyClick: openVerify,
    topSlot: bannersSlot,
  });

  // Show empty shell while loading. Replaces the older full-page skeleton
  // which would unmount-then-mount the entire landing tree (visible flicker).
  if (contentLoading || dataLoading) {
    return <div className={`min-h-screen ${bgMain}`} aria-busy="true" />;
  }

  return (
    <div className={`min-h-screen ${bgMain} ${textMain} relative overflow-x-clip`}>
      {/* Google One Tap Container - positioned at top right */}
      <div id="oneTapContainer" className="fixed top-4 right-4 z-[100]" />

      {/* Hero — Brand-blue, compact, static.
          Same visual identity as before (blue gradient slab + decorative
          radial shapes + title accent + featured/resume side card) but
          everything is sized down and the eyebrow chip + live-stats strip
          are removed. No CSS animations: blobs, fade-up entrances, glow
          pulses, and the headline shimmer have all been replaced with a
          static fill so the hero feels calm. */}
      {isLandingVisible('hero') && (
      <section
        className="pt-20 pb-10 px-4 relative overflow-x-clip"
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <div
            className="relative overflow-hidden rounded-[14px] border border-white/10"
            data-testid="hero-card"
            style={{
              background: 'linear-gradient(135deg, #0d3a71 0%, #114d97 38%, #1560bd 78%, #1f6dc7 100%)',
              boxShadow: '0 14px 40px -22px rgba(13,58,113,0.5)',
            }}
          >
            {/* Fancy background decorations (blob glow + dotted texture)
             * were removed per product request — the hero now uses only
             * the solid brand-gradient set on the parent. Right-side SVG
             * pattern was removed too. */}

            <div className="relative px-5 sm:px-8 pt-7 pb-7">
              <div className="min-w-0 relative z-10 max-w-[58ch]">
                {/* Headline — keep both halves of the CMS title on a
                    single horizontal line. The last word gets a brand
                    blue gradient text fill so the accent reads as part
                    of the same theme without breaking layout. */}
                {(() => {
                  const raw = (siteContent.hero_title || 'Test yourself. Master anything.').trim();
                  const lastSpace = raw.lastIndexOf(' ');
                  const head = lastSpace > 0 ? raw.slice(0, lastSpace) : '';
                  const accent = lastSpace > 0 ? raw.slice(lastSpace + 1) : raw;
                  return (
                    <h1
                      className="text-[18px] sm:text-[20px] lg:text-[22px] font-medium leading-[1.2] tracking-tight text-white mb-2 whitespace-nowrap overflow-hidden text-ellipsis"
                      data-testid="hero-title"
                    >
                      {head && <span>{head} </span>}
                      <span
                        className="font-semibold"
                        style={{
                          // Brand-blue gradient text. `background-clip:
                          // text` masks the gradient to the glyph shapes
                          // and `WebkitTextFillColor: transparent` lets
                          // the gradient show through (transparent text +
                          // tinted background). Same trick used on
                          // gradient logos.
                          background: 'linear-gradient(90deg, #75abe7 0%, #a3c7ef 45%, #e6f0fb 100%)',
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          color: 'transparent',
                        }}
                        data-testid="hero-title-accent"
                      >
                        {accent}
                      </span>
                    </h1>
                  );
                })()}

                <p
                  className="text-[12.5px] sm:text-[13px] leading-relaxed mb-4 max-w-[44ch]"
                  style={{ color: 'rgba(232,241,251,0.85)' }}
                >
                  Learn, create and have fun in one platform.
                </p>

                {/* CTAs */}
                <div className="flex flex-wrap items-center gap-2.5">
                  <button
                    onClick={() => {
                      if (sectionCardRef.current) {
                        sectionCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-4 h-9 rounded-[8px] text-[12.5px] font-medium bg-white text-[#0d3a71] hover:bg-[#e8f1fb]"
                    data-testid="hero-start-btn"
                  >
                    {siteContent.hero_cta || 'Get Started'}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => navigate('/subscription')}
                    className="inline-flex items-center gap-1.5 px-4 h-9 rounded-[8px] text-[12.5px] font-medium border border-white/35 text-white hover:bg-white/10"
                    data-testid="hero-subscribe-btn"
                    title={heroSubActive && heroSub?.expires_at ? `Subscription active until ${new Date(heroSub.expires_at).toLocaleString()}` : 'Subscribe for full access'}
                  >
                    <Clock className="w-3 h-3" />
                    {getText('hero_subscribe_cta', 'Subscribe')}
                    {heroSubRemainingLabel && (
                      <span
                        className="ml-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold tabular-nums leading-none bg-emerald-400 text-emerald-950 border border-emerald-300"
                        data-testid="hero-subscribe-remaining"
                      >
                        {heroSubRemainingLabel}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Right-end decorative SVG pattern was removed per product
                  request to keep the hero minimal. */}
            </div>
          </div>

          {/* Live Now Banner — hidden on the Games tab (the tab itself
              already lists live lobbies). Reflects room status:
              · lobby   → "Join room"
              · live    → "Observe"
              · finished/expired → not rendered (filtered upstream) */}
          {liveSession && activeSection !== 'live-games' && (() => {
            const st = (liveSession.status || 'lobby').toLowerCase();
            const isObserve = st === 'live';
            const ctaLabel = isObserve ? 'Observe' : 'Join room';
            const goRoom = () => {
              const code = liveSession.room_code || liveSession.session_id;
              if (!code) return;
              navigate(`/live/${code}`);
            };
            return (
            <div
              className="flex items-center gap-4 px-4 py-[13px] mt-[14px] rounded-[12px] overflow-hidden"
              style={{ background: '#E6F5EE' }}
              data-testid="live-banner"
              data-status={st}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="relative flex-shrink-0 w-2.5 h-2.5">
                  <span className="absolute inset-0 rounded-full animate-[livePulse_1.5s_ease-in-out_infinite]" style={{ background: '#5DCAA5' }} />
                  <span className="absolute inset-0 rounded-full" style={{ background: '#5DCAA5' }} />
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-gray-800 truncate">
                    {isObserve ? 'In progress' : 'Live now'} · {liveSession.session_name || liveSession.title || 'Quiz Session'}
                  </p>
                  <p className="text-[11px] truncate" style={{ color: '#5A7A6A' }}>
                    {liveSession.location || 'Online'} · {liveSession.player_count || (liveSession.players || []).length || 0} players · {liveSession.time_ago || 'just now'}
                  </p>
                </div>
              </div>
              <div className="hidden sm:flex items-center -space-x-2 flex-shrink-0">
                {(liveSession.players || []).slice(0, 3).map((p, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border-2"
                    style={{ background: ['#B8DCC8','#A0CCB4','#8ABCA0'][i], borderColor: '#E6F5EE', color: '#2D5A42', zIndex: 3 - i }}
                  >
                    {(p.name || '?').substring(0, 2).toUpperCase()}
                  </div>
                ))}
                {((liveSession.player_count || (liveSession.players || []).length || 0)) > 3 && (
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium border-2"
                    style={{ background: '#C4E4D2', borderColor: '#E6F5EE', color: '#5A7A6A' }}
                  >
                    +{(liveSession.player_count || (liveSession.players || []).length || 0) - 3}
                  </div>
                )}
              </div>
              <button
                onClick={goRoom}
                className="flex-shrink-0 px-4 py-2 text-[13px] font-medium rounded-[8px]"
                style={{ background: '#2D5A42', color: '#FFFFFF' }}
                data-testid="live-join-btn"
              >
                {ctaLabel}
              </button>
            </div>
            );
          })()}

          {/* Section Tabs — wrapped in card */}
          <div ref={sectionCardRef} className={`mt-4 mb-4 rounded-[14px] border p-4 ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div
            className={`sticky z-30 flex items-center gap-1 p-1 rounded-[10px] mb-4 -mx-4 px-4 -mt-4 pt-4 ${isDark ? 'bg-gray-900/95 backdrop-blur-md' : 'bg-white/95 backdrop-blur-md'}`}
            style={{ top: visibleBanners.length > 0 ? 96 : 56 }}
            data-testid="section-tabs"
          >
            <div className={`flex items-center gap-1 p-1 rounded-[10px] flex-1 relative ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
            {(() => {
              // Admins can toggle individual tabs off via CMS → Landing Page
              // Sections. We hide any tab whose key is disabled; if the user
              // happens to be on a tab that just got disabled, redirect them
              // to the first visible tab so the page never shows an empty
              // state. Default behaviour (when nothing's set) is "show all".
              const allTabs = [
                { id: 'courses', label: getText('tab_courses', 'Courses'), cmsKey: 'tab_courses' },
                { id: 'quick-quiz', label: getText('tab_quick_quiz', 'Quick Quiz'), cmsKey: 'tab_quick_quiz' },
                { id: 'live-games', label: getText('tab_games', 'Games'), cmsKey: 'tab_games' },
              ];
              return allTabs.filter(t => isLandingVisible(t.cmsKey));
            })().map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex-1 py-2 px-3 rounded-[8px] text-[13px] font-medium ${
                  activeSection === tab.id
                    ? isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900 shadow-sm'
                    : isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
            {/* Active-tab scroll progress underline */}
            <div
              className="absolute left-1 right-1 bottom-[-3px] h-[2px] rounded-full overflow-hidden"
              aria-hidden="true"
              style={{ opacity: tabProgress > 0 ? 1 : 0, transition: 'opacity 0.2s ease' }}
              data-testid="tab-progress-track"
            >
              <div
                className={`h-full ${isDark ? 'bg-brand-700/60' : 'bg-brand-300/70'}`}
                style={{ width: `${(tabProgress * 100).toFixed(2)}%`, transition: 'width 0.05s linear' }}
                data-testid="tab-progress-fill"
              />
            </div>
            </div>
          </div>

          {/* Courses Section */}
          {activeSection === 'courses' && (<>
          {/* Category pills row + a search icon trigger on the right.
              The search icon expands into a single-line input that
              further filters topic cards by name / description /
              category — composes on top of the selected category pill. */}
          {(filteredTopics || []).length > 0 && (() => {
            // Build pills from the actual topic categories present in the
            // current view — earlier we hardcoded a fixed allowlist
            // (`education / driving / health / language / business / custom`)
            // which silently dropped any category that didn't match
            // verbatim. In production that meant topics with categories
            // like `Music`, `Sport`, `Languages` (plural), etc. wouldn't
            // get a pill even though they showed up in the grid.
            const titleCase = (s) =>
              String(s || '').trim().replace(/\b\w/g, (c) => c.toUpperCase());
            const seen = new Map(); // id -> label
            for (const t of filteredTopics || []) {
              const raw = (t.category || '').trim();
              if (!raw) continue;
              const id = raw.toLowerCase();
              if (!seen.has(id)) seen.set(id, titleCase(raw));
            }
            const visible = Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
            return (
              <div className="flex items-center gap-2 mb-4">
                {/* Pills — hidden on small screens once the search box is
                    open so the input + close button can claim the full row.
                    On sm+ they always render alongside the search. */}
                <div className={`items-center gap-2 overflow-x-auto pb-1 scrollbar-none flex-1 min-w-0 ${
                  topicSearchOpen ? 'hidden sm:flex' : 'flex'
                }`}>
                  <button
                    onClick={() => setCategoryFilter('all')}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap flex-shrink-0 ${
                      categoryFilter === 'all'
                        ? 'bg-[#E6F1FB] text-[#0C447C]'
                        : isDark ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-white text-gray-500 border border-gray-200'
                    }`}
                    data-testid="filter-all"
                  >
                    All
                  </button>
                  {visible.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setCategoryFilter(cat.id)}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap flex-shrink-0 ${
                        categoryFilter === cat.id
                          ? 'bg-[#E6F1FB] text-[#0C447C]'
                          : isDark ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-white text-gray-500 border border-gray-200'
                      }`}
                      data-testid={`filter-${cat.id}`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
                {topicSearchOpen ? (
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full border w-full sm:w-auto sm:flex-shrink-0 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <input
                      autoFocus
                      type="text"
                      value={topicSearch}
                      onChange={(e) => setTopicSearch(e.target.value)}
                      placeholder="Search courses…"
                      className={`text-[12px] flex-1 min-w-0 sm:w-44 sm:flex-none bg-transparent border-none outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                      data-testid="topic-search-input"
                    />
                    <button
                      type="button"
                      onClick={() => { setTopicSearch(''); setTopicSearchOpen(false); }}
                      className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                      aria-label="Close search"
                      data-testid="topic-search-close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setTopicSearchOpen(true)}
                    className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                      isDark
                        ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                        : 'bg-white border-gray-200 text-gray-500 hover:text-[#1560bd] hover:border-brand-200'
                    }`}
                    aria-label="Search courses"
                    data-testid="topic-search-trigger"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })()}


          {/* Topic Path Cards. When we're still loading the catalogue
              we render 6 skeleton placeholders so the layout doesn't
              jump as data arrives. Each skeleton mirrors the shape of
              a real card (top accent strip + 3 text rows + CTA).    */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[10px] mt-5 mb-3" data-testid="topic-cards-grid">
            {topicsLoading && filteredTopics.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`skel-${i}`}
                  className={`relative rounded-[12px] overflow-hidden border animate-pulse ${
                    isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
                  }`}
                  data-testid={`topic-card-skeleton-${i}`}
                >
                  <div className={`absolute top-0 inset-x-0 h-1 ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
                  <div className="p-3.5 pt-4 space-y-2">
                    <div className={`h-2.5 w-16 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
                    <div className={`h-3.5 w-3/4 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
                    <div className={`h-2.5 w-1/2 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
                    <div className={`h-1 w-full rounded mt-3 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`} />
                    <div className={`h-8 w-full rounded-[8px] mt-2 ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
                    <div className={`h-2 w-1/3 mx-auto rounded mt-1.5 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`} />
                  </div>
                </div>
              ))
            ) : (
              filteredTopics
              .filter(t => categoryFilter === 'all' || (t.category || '').toLowerCase() === categoryFilter)
              .filter(t => {
                const q = topicSearch.trim().toLowerCase();
                if (!q) return true;
                return (
                  String(getTranslatedContent(t, 'name') || t.name || '').toLowerCase().includes(q) ||
                  String(getTranslatedContent(t, 'description') || t.description || '').toLowerCase().includes(q) ||
                  String(t.category || '').toLowerCase().includes(q)
                );
              })
              // Respect the admin-assigned display order returned by the
              // backend (sorted by `order` asc). No further client-side
              // re-sorting so what admins arrange is what users see.
              .map((topic) => {
              const isComingSoon = topic.status === 'coming_soon';
              // Has the signed-in user actually engaged with this topic?
              // We treat "started" as: backend marked it started, the user
              // has any mastery progress, or they've recorded at least one
              // attempt. Guests never get the green resume treatment —
              // their progress is anonymous + ephemeral.
              const isResumed = !!loggedInUser && !isComingSoon && (
                topic.started === true ||
                (topic.mastery_pct || 0) > 0 ||
                (topic.avg_score || 0) > 0 ||
                (topic.total_attempts || 0) > 0
              );
              // Two-tone theme palette only:
              //   • BRAND blue is the primary identity for every active card.
              //   • CYAN is the secondary accent (CTA gradient in dark, mastery
              //     highlights, "Resume"/"New" tags).
              //   • COMING uses a neutral grey scale (no extra hue).
              const BRAND = { from: '#0d4a8a', to: '#1a6db5', chip: '#cfe2f7', shine: '#e6f0fb' };
              const COMING = { from: '#475569', to: '#94a3b8', chip: '#e2e8f0', shine: '#f1f5f9' };
              // ACCENT cyan is the only second hue allowed on the card.
              const ACCENT = '#22d3ee';
              const cat = isComingSoon ? COMING : BRAND;
              // Real subtopic + pre-set quiz counts from the backend.
              const subtopicCount = topic.total_subtopics ?? topic.subtopic_count ?? 0;
              const quizCount = topic.static_quizzes_count ?? topic.quizzes_count ?? 0;
              const hasCert = topic.has_certificate !== false;
              // `progress` now mirrors the topic mastery bar.
              const progress = Math.round(topic.mastery_percentage ?? topic.progress ?? 0);
              const completed = topic.completed_subtopics || 0;
              const total = topic.total_subtopics || 0;
              const badge = topic.badge || (isComingSoon ? null : (topic.is_official ? 'Official' : null));
              // Random geometric design — simple deterministic gradient based on topic id
              const seed = String(topic.topic_id || topic.name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
              const designIdx = seed % 4;

              // Two-mode surface:
              //   • Light: clear white card, dark brand text — keeps the
              //     landing section airy and readable.
              //   • Dark : solid brand-blue surface, white text — matches
              //     the reference UI.
              // Coming-soon falls back to a neutral grey scale in both modes.
              const isLightCard = !isDark;
              return (
                <div
                  key={topic.topic_id}
                  onClick={() => {
                    if (isComingSoon) {
                      setComingSoonTapped(topic.topic_id);
                      setTimeout(() => setComingSoonTapped(null), 2000);
                    } else {
                      handleTopicSelect(topic);
                    }
                  }}
                  className={`group relative rounded-[10px] overflow-hidden border transition-all flex flex-col ${
                    isLightCard
                      ? isComingSoon
                        ? 'bg-white border-gray-200'
                        : 'bg-white border-gray-200 hover:border-brand-300 hover:shadow-[0_10px_24px_-14px_rgba(13,74,138,0.35)]'
                      : isComingSoon
                        ? 'border-slate-700'
                        : 'border-slate-600/60 hover:border-slate-500'
                  } ${isComingSoon ? 'cursor-default opacity-95' : 'cursor-pointer'}`}
                  style={{
                    background: isLightCard
                      ? undefined
                      : `linear-gradient(160deg, ${isComingSoon ? '#334155' : '#0d4a8a'} 0%, ${isComingSoon ? '#1e293b' : '#0a3d72'} 100%)`,
                    boxShadow: isLightCard
                      ? undefined
                      : '0 10px 28px -16px rgba(4, 19, 37, 0.55)',
                    minHeight: 132,
                  }}
                  data-testid={`topic-card-${topic.topic_id}`}
                >
                  {/* Coming Soon Lock Overlay */}
                  {isComingSoon && comingSoonTapped === topic.topic_id && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/55 rounded-[10px]">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white">
                          <Lock className="w-5 h-5 text-brand-500" />
                        </div>
                        <span className="text-white text-sm font-medium">Coming Soon</span>
                      </div>
                    </div>
                  )}

                  {/* Content stack — `flex-1` pushes the CTA row to the
                      bottom so every card's button sits on the same baseline
                      regardless of title/subtext length. */}
                  <div className="relative p-3 flex-1 flex flex-col gap-2.5">
                    {/* Title panel — title sits on a subtle brand-tinted
                        backdrop (light mode) / glassy white tint (dark
                        mode), mirroring the user-provided reference. The
                        `i` icon stays anchored to the panel's top-right. */}
                    <div
                      className={`relative rounded-[8px] px-3 py-2.5 flex items-center justify-between gap-2.5 ${
                        isLightCard
                          ? 'bg-[#eef4fb]'
                          : 'bg-white/[0.08]'
                      }`}
                    >
                      {topic.image_url && (
                        <div
                          className={`flex-shrink-0 w-10 h-10 rounded-md overflow-hidden ${
                            isLightCard ? 'bg-white/60' : 'bg-white/10'
                          }`}
                          data-testid={`topic-thumb-${topic.topic_id}`}
                        >
                          <img
                            src={topic.image_url}
                            alt=""
                            width={40}
                            height={40}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <h3
                        className={`text-[12px] font-semibold leading-snug line-clamp-2 flex-1 min-w-0 ${
                          isLightCard ? 'text-[#1560bd]' : 'text-white'
                        }`}
                      >
                        {getTranslatedContent(topic, 'name')}
                      </h3>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoTopic(topic);
                        }}
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 ${
                          isLightCard
                            ? 'text-brand-500 hover:bg-white/70'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }`}
                        aria-label="Topic info"
                        data-testid={`topic-info-${topic.topic_id}`}
                      >
                        <Info className="w-[16px] h-[16px]" strokeWidth={1.75} />
                      </button>
                    </div>

                    {/* Single-line subtext — always shows the topic's
                        description (translated to the active language).
                        Falls back to the category name only if there is
                        truly no description on the document. The CTA
                        button below already conveys the action state
                        (Start / Continue / Resume) — duplicating that on
                        the subtext line was confusing, so we drop the
                        `isResumed` override here. */}
                    <p
                      className={`text-[12px] line-clamp-1 px-1 ${
                        isLightCard
                          ? 'text-gray-500'
                          : 'text-white/70'
                      }`}
                    >
                      {getTranslatedContent(topic, 'description') ||
                       (topic.category || 'Course').replace(/^./, c => c.toUpperCase())}
                    </p>

                    {/* Spacer — pushes CTA to the bottom edge */}
                    <div className="flex-1 min-h-[4px]" />

                    {/* CTA row: a slim progress bar shares space with the
                        primary action. The bar is hidden for logged-out
                        guests and Coming-Soon topics where it would be
                        meaningless. Gap is 5px per design spec. */}
                    <div className="flex items-center w-full" style={{ gap: '5px' }}>
                      {!isComingSoon && !!loggedInUser && (
                        <div
                          className={`flex-1 h-1 rounded-full overflow-hidden ${
                            progress > 0
                              ? (isLightCard ? 'bg-brand-100' : 'bg-white/15')
                              : `bg-transparent border ${isLightCard ? 'border-brand-100' : 'border-white/20'}`
                          }`}
                          data-testid={`topic-progress-${topic.topic_id}`}
                          aria-label={`Mastery progress for ${topic.name}`}
                          role="progressbar"
                          aria-valuenow={Math.round(progress)}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          {progress > 0 && (
                            <div
                              className={`h-full rounded-full transition-all ${
                                isLightCard ? 'bg-[#1560bd]' : 'bg-gradient-to-r from-cyan-400 to-blue-500'
                              }`}
                              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                            />
                          )}
                        </div>
                      )}

                      {/* Primary CTA — text-only, right-aligned, with the
                          previously requested soft brand-tinted outline so
                          the action affordance is visible without a fill. */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isComingSoon) {
                            setComingSoonTapped(topic.topic_id);
                            setTimeout(() => setComingSoonTapped(null), 2000);
                          } else {
                            handleTopicSelect(topic);
                          }
                        }}
                        disabled={isComingSoon}
                        className={`ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-[8px] text-[12px] font-semibold transition-colors bg-transparent border flex-shrink-0 ${
                          isComingSoon
                            ? isLightCard
                              ? 'text-gray-400 border-gray-200'
                              : 'text-white/50 border-white/10'
                            : isLightCard
                              ? 'text-[#1560bd] border-brand-100 hover:border-brand-200 hover:text-[#0d3a71]'
                              : 'text-white border-white/20 hover:border-white/40'
                        }`}
                        data-testid={`topic-cta-${topic.topic_id}`}
                      >
                        <span>
                          {isComingSoon
                            ? getText('card_coming_soon', 'Coming Soon')
                            : progress > 0
                              ? getText('card_continue_btn', 'Continue')
                              : topic.started ? getText('card_resume_btn', 'Resume') : getText('card_start_now', 'Start Now')}
                        </span>
                        {!isComingSoon && <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.25} />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
            )}
          </div>
          </>)}

          {/* Quick Quiz Section */}
          {activeSection === 'quick-quiz' && (<>
            {/* Section Header */}
            <div className="flex items-center justify-between mb-3">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Quick Quizzes</span>
              <button
                onClick={() => navigate('/quizzes')}
                className="text-[12px] font-medium text-brand-500"
                data-testid="see-all-quizzes-btn"
              >
                See all {quickQuizzes.length > 0 ? quickQuizzes.length : ''}{quickQuizzes.length >= 8 ? '+' : ''}
              </button>
            </div>

            {/* Search bar + language picker.
                The picker is a tight inline control so we don't add
                visual noise on mobile — the dropdown collapses to icon-
                ish width when narrow. */}
            <div className={`flex items-center gap-2 rounded-[12px] border px-3 py-2 mb-2 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <Search className={`w-4 h-4 ${textMuted}`} />
              <input
                value={quickQuizSearch}
                onChange={(e) => setQuickQuizSearch(e.target.value)}
                placeholder="Search quizzes…"
                className={`flex-1 bg-transparent text-[13px] outline-none ${textMain}`}
                data-testid="home-quiz-search"
              />
              {quickQuizSearch && (
                <button onClick={() => setQuickQuizSearch('')} className={`text-[11px] ${textMuted} hover:underline`}>Clear</button>
              )}
              <span className={`hidden sm:inline-block w-px h-4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <select
                value={quickQuizLanguage}
                onChange={(e) => { langSyncRef.current = false; setQuickQuizLanguage(e.target.value); }}
                className={`text-[11.5px] px-1.5 py-0.5 rounded bg-transparent focus:outline-none ${textMuted}`}
                data-testid="home-quiz-lang"
                aria-label="Filter by language"
              >
                <option value="all">All langs</option>
                <option value="en">English</option>
                <option value="rw">Kinyarwanda</option>
                <option value="fr">French</option>
              </select>
            </div>

            {/* Category pills — only show categories that exist in loaded quizzes */}
            {(() => {
              const present = new Set([...(quickQuizzes || []), ...(extraQuizzes || [])].map(q => (q.category || '').toLowerCase()).filter(Boolean));
              const ordered = ['education', 'driving', 'health', 'language', 'business', 'custom'].filter(c => present.has(c));
              if (ordered.length === 0 && (quickQuizzes || []).length === 0) return null;
              return (
                <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-3" style={{ scrollbarWidth: 'none' }}>
                  <button
                    onClick={() => setQuickQuizCategory('all')}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
                      quickQuizCategory === 'all'
                        ? 'bg-brand-500 text-white border-brand-500'
                        : isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
                    }`}
                    data-testid="home-quiz-pill-all"
                  >
                    All
                  </button>
                  {ordered.map((c) => (
                    <button
                      key={c}
                      onClick={() => setQuickQuizCategory(c)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors capitalize ${
                        quickQuizCategory === c
                          ? 'bg-brand-500 text-white border-brand-500'
                          : isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
                      }`}
                      data-testid={`home-quiz-pill-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* Quiz Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(() => {
                const term = quickQuizSearch.trim().toLowerCase();
                // Combine initial trending + appended pages, dedupe by slug
                const combined = [];
                const seen = new Set();
                for (const q of [...quickQuizzes, ...extraQuizzes]) {
                  const key = q.slug || q.quiz_id;
                  if (key && !seen.has(key)) { seen.add(key); combined.push(q); }
                }
                const filtered = combined.filter((q) => {
                  const cat = (q.category || '').toLowerCase();
                  if (quickQuizCategory !== 'all' && cat !== quickQuizCategory) return false;
                  // Language filter: when a specific UI language is chosen
                  // (anything other than "All languages") we narrow to
                  // quizzes whose `language` matches exactly. Legacy
                  // quizzes without a declared language are treated as
                  // English so they still surface for EN viewers and are
                  // hidden for FR/RW (matches the backend's `or "en"`
                  // default in /landing/quick-quizzes).
                  if (quickQuizLanguage !== 'all') {
                    const qLang = (q.language || 'en').toLowerCase();
                    if (qLang !== quickQuizLanguage) return false;
                  }
                  if (term) {
                    const hay = `${q.title || q.name || ''} ${q.description || ''} ${q.creator_name || ''}`.toLowerCase();
                    if (!hay.includes(term)) return false;
                  }
                  return true;
                });
                if (filtered.length === 0 && (term || quickQuizCategory !== 'all')) {
                  return (
                    <div className={`col-span-full rounded-[12px] border p-6 text-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`} data-testid="home-quiz-empty-filter">
                      <p className={`text-[13px] font-medium mb-1 ${textMain}`}>No quizzes match</p>
                      <p className={`text-[12px] ${textMuted}`}>Try a different category or search term.</p>
                    </div>
                  );
                }
                return filtered.map((quiz) => {
                // Single neutral badge style for all types, with a small dot indicator
                const typeBadges = {
                  official: { dot: '#0d4a8a', label: 'By Ikizamini' },
                  community: { dot: '#9ca3af', label: 'Community' },
                  assigned: { dot: '#10b981', label: 'Assigned' },
                };
                const badge = typeBadges[(quiz.type || 'official').toLowerCase()] || typeBadges.official;
                const qCount = quiz.question_count || quiz.questions?.length || 0;
                const attempts = quiz.total_attempts || 0;
                const taken = getTakenQuizzes();
                const hasTaken = quiz.slug && taken[quiz.slug];
                const isLiked = quiz.slug && likedQuizzes[quiz.slug];
                const likes = (quizStats[quiz.slug]?.likes ?? quiz.likes) || 0;

                return (
                  <VirtualSlot key={quiz.quiz_id || quiz.slug} minHeight={150} testId={`virtual-slot-${quiz.quiz_id || quiz.slug}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`rounded-[12px] border p-3 cursor-pointer transition-colors ${isDark ? 'bg-gray-900 border-gray-800 hover:border-gray-700' : 'bg-white border-gray-200 hover:border-brand-300'}`}
                    data-testid={`quiz-card-${quiz.quiz_id}`}
                    onClick={async () => {
                      if (quiz.safe_questions && quiz.safe_questions.length > 0) {
                        setActiveQuickQuiz({ ...quiz, questions: quiz.safe_questions });
                      } else if (quiz.slug) {
                        setLoadingQuiz(true);
                        try {
                          const res = await axios.get(`${API}/quizzes/${quiz.slug}`);
                          setActiveQuickQuiz(res.data);
                        } catch { toast.error('Could not load quiz'); }
                        finally { setLoadingQuiz(false); }
                      } else {
                        if (isAuthenticated) navigate('/dashboard');
                        else setShowAuth(true);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.currentTarget.click();
                      }
                    }}
                  >
                    {/* Top: badges */}
                    <div className="flex items-center gap-1.5 mb-2">
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: badge.dot }} />
                        {badge.label}
                      </span>
                      {hasTaken && (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                          {hasTaken.score}% ✓
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h4 className={`text-[12px] font-medium leading-[1.4] mb-1.5 ${textMain}`}>
                      {quiz.title || quiz.name}
                    </h4>

                    {/* Meta */}
                    <p className={`text-[11px] ${textMuted}`}>
                      {qCount} questions · {attempts > 0 ? `${attempts} attempts` : 'New'}{quiz.type === 'community' && quiz.creator_name ? ` · by ${quiz.creator_name}` : ''}
                    </p>

                    {/* Views / Likes / Actions row */}
                    <div className="flex items-center justify-between mt-2.5 pt-2 border-t" style={{ borderColor: isDark ? '#1f2937' : '#f3f4f6' }}>
                      <div className="flex items-center gap-3">
                        {/* Attempts */}
                        <span className={`flex items-center gap-1 text-[10px] ${textMuted}`}>
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M6 3l6 5-6 5V3z" fill="currentColor"/></svg>
                          {attempts}
                        </span>
                        {/* Like button */}
                        {quiz.likes_enabled !== false && (
                        <button
                          onClick={(e) => quiz.slug && toggleLike(quiz.slug, e)}
                          className={`flex items-center gap-1 text-[10px] ${isLiked ? 'text-red-500' : textMuted}`}
                          data-testid={`like-${quiz.quiz_id}`}
                        >
                          <svg width="12" height="12" viewBox="0 0 16 16" fill={isLiked ? 'currentColor' : 'none'}><path d="M8 14s-5.5-3.5-5.5-7A3 3 0 018 4.5 3 3 0 0113.5 7C13.5 10.5 8 14 8 14z" stroke="currentColor" strokeWidth="1.2"/></svg>
                          {likes}
                        </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {quiz.live_enabled && (
                          <span className={`inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-gray-800 text-brand-300' : 'bg-gray-100 text-brand-600'}`}>
                            <span className="w-1 h-1 rounded-full bg-brand-500" />
                            Live
                          </span>
                        )}
                        {hasTaken && quiz.slug && quiz.leaderboard_enabled !== false && (
                          <button
                            onClick={(e) => { e.stopPropagation(); viewLeaderboard(quiz.slug); }}
                            className="text-[10px] font-medium text-brand-500"
                            data-testid={`leaderboard-${quiz.quiz_id}`}
                          >
                            Ranks
                          </button>
                        )}
                        {loggedInUser && quiz.created_by && loggedInUser.user_id === quiz.created_by && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/manage-quiz/${quiz.slug}`); }}
                            className={`text-[10px] font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                            data-testid={`manage-${quiz.quiz_id}`}
                          >
                            Manage
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  </VirtualSlot>
                );
              });
              })()}

              {/* "Create your own" card — appended at the end of the
                  grid (when there's at least one real quiz). Reinforces
                  the "you can publish too" CTA without taking up the
                  empty-state slot. */}
              {(quickQuizzes.length > 0 || extraQuizzes.length > 0) && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/create')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/create'); } }}
                  data-testid="quiz-card-create-your-own"
                  className={`rounded-[12px] border-2 border-dashed p-3 cursor-pointer flex flex-col items-center justify-center text-center min-h-[140px] transition-colors ${
                    isDark
                      ? 'border-gray-700 hover:border-brand-500 bg-gray-900/40 text-gray-300'
                      : 'border-gray-300 hover:border-brand-500 bg-white text-gray-600'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 ${isDark ? 'bg-brand-900/40 text-brand-300' : 'bg-brand-50 text-brand-500'}`}>
                    <Plus className="w-4 h-4" />
                  </div>
                  <p className={`text-[12.5px] font-medium ${textMain}`}>Create your own quiz</p>
                  <p className={`text-[10.5px] mt-1 ${textMuted}`}>5-20 questions · AI-assisted · public link</p>
                </div>
              )}
            </div>

            {/* Empty state */}
            {quickQuizzes.length === 0 && !activeQuickQuiz && (
              <div className={`mt-3 rounded-[12px] border p-6 text-center ${cardBg}`}>
                <p className={`text-[13px] font-medium mb-1 ${textMain}`}>No quizzes yet</p>
                <p className={`text-[12px] ${textMuted} mb-3`}>Be the first to create one!</p>
                <Button size="sm" onClick={() => { navigate('/create'); }} className="bg-brand-500 hover:bg-brand-600 text-white rounded-[10px] px-5 text-[13px] font-medium">
                  Create a quiz
                </Button>
              </div>
            )}

            {/* Inline Quiz Player */}
            {loadingQuiz && (
              <div className={`mt-3 rounded-[12px] border p-6 text-center ${cardBg}`}>
                <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className={`text-[12px] ${textMuted}`}>Loading quiz...</p>
              </div>
            )}

            {/* View More Button */}
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={loadMoreQuizzes}
                disabled={loadingMore || !hasMoreQuizzes}
                className={`rounded-[10px] px-6 text-[13px] font-medium border ${isDark ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-600'}`}
                data-testid="view-more-quizzes-btn"
              >
                {loadingMore ? 'Loading…' : !hasMoreQuizzes ? 'You\u2019ve reached the end' : 'View more quizzes'}
              </Button>
            </div>
          </>)}

          {/* Live Games Section */}
          {activeSection === 'live-games' && (() => {
            // Most-active live lobby = first non-finished session sorted by
            // player_count desc. We only show one card on the front page —
            // a "View all live lobbies →" CTA opens the dedicated list.
            const order = { live: 0, soon: 1, full: 2, finished: 3 };
            const sortedReal = (liveSessions || [])
              .slice()
              .sort((a, b) => {
                const sa = order[a.status || 'live'] ?? 9;
                const sb = order[b.status || 'live'] ?? 9;
                if (sa !== sb) return sa - sb;
                return (b.player_count || 0) - (a.player_count || 0);
              });
            const topLobby = sortedReal.find(s => (s.status || 'live') !== 'finished') || null;

            return (
              <div>
                {/* "View all live lobbies" — link only, no card. The
                    dedicated /live-lobbies page lists every active session. */}
                {!activeGameMode && (
                  <div className="mb-4 flex items-center justify-end" data-testid="view-all-live-link-row">
                    <button
                      onClick={() => navigate('/live-lobbies')}
                      className="text-[12px] font-medium text-emerald-600 hover:text-emerald-700"
                      data-testid="view-all-live-lobbies-link"
                    >
                      View all live lobbies →
                    </button>
                  </div>
                )}

                {/* Game-mode cards — mirror the topic-card layout:
                    [icon box (#eef4fb)] [game name + Start CTA] [i icon].
                    Icons + text use the brand-blue `#1560bd`. Click "Start"
                    navigates to the game page; saved choice (category +
                    play type) skips the picker for return visits. */}
                {!activeGameMode && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="gamemode-grid">
                  {[
                    {
                      id: 'knowledge-climb',
                      label: 'Aviator',
                      blurb: 'Cash out before the crash',
                      Icon: Plane,
                      instructions: [
                        'A plane lifts off — each round has a random climb speed and a random crash point.',
                        'Cash out at any moment to bank the current multiplier × answer time.',
                        'Each correct answer adds multiplier points; a wrong answer costs you 1 life.',
                        'If the plane crashes before you cash out, you lose the round.',
                        'Filling the progress bar opens a red envelope and reveals a real prize.',
                      ],
                      prize_rule: 'Hit the gift threshold (points bar full) to claim a prize. First-time players are guaranteed a special starter gift.',
                    },
                    {
                      id: 'speed-sprint',
                      label: 'Speed typing',
                      blurb: 'Type fast · beat the clock',
                      Icon: Keyboard,
                      instructions: [
                        'A short answer appears with one word blanked out as an input.',
                        'Type the missing word and press Enter — the rest of the sentence is your hint.',
                        'You score `speed × accuracy` — faster correct answers stack a multiplier.',
                        'Wrong answers cost you streak bonus, not lives.',
                        'Climb the points bar to unlock a gift.',
                      ],
                      prize_rule: 'Cross the points threshold set by the admin during a single run to unlock a gift.',
                    },
                    {
                      id: 'rocket-race',
                      label: 'Rocket race',
                      blurb: 'Multiplayer · first to orbit',
                      Icon: Rocket,
                      instructions: [
                        'Each correct answer adds fuel to your rocket.',
                        'The fastest correct answer per round earns a speed bonus.',
                        'Wrong answers vent fuel — the rocket dips down the track.',
                        'First pilot to fill the fuel bar reaches orbit and wins the race.',
                        'Winning the race or filling the bar above the threshold earns a gift.',
                      ],
                      prize_rule: 'Fill the fuel bar past the gift threshold (or finish the race in 1st place) to unlock a prize.',
                    },
                    {
                      id: 'tug-of-war',
                      label: 'Tug of war',
                      blurb: 'Team battle · pull the rope',
                      Icon: Swords,
                      instructions: [
                        'Two teams take turns answering quiz questions.',
                        'Every correct answer pulls the rope one notch toward your side.',
                        'Wrong answers freeze your team for the round.',
                        'First team to drag the rope across the finish line wins the match.',
                        'Earn a prize when your team crosses the gift threshold.',
                      ],
                      prize_rule: 'Reach the rope-points threshold set by the admin to unlock a gift drawn from the rewards pool.',
                    },
                  ]
                    .filter(mode => isGameEnabled(mode.id))
                    .sort((a, b) => sortGamesByOrder([a, b])[0].id === a.id ? -1 : 1)
                    .map(mode => {
                    const { Icon } = mode;
                    // Saved choices for this mode (per-game). When present
                    // we skip the picker and jump straight to play. On the
                    // user's very first Start of a mode, we instead open
                    // the instructions modal so they understand the rules
                    // (and prize logic) before being thrown into a run.
                    const startGame = (e) => {
                      e?.stopPropagation();
                      let seen = false;
                      try { seen = localStorage.getItem(`game_instructions_seen_${mode.id}`) === '1'; } catch (_) { /* ignore */ }
                      if (!seen) { setGameInfoMode(mode); return; }
                      try {
                        const saved = JSON.parse(localStorage.getItem(`game_prefs_${mode.id}`) || 'null');
                        if (saved?.category && saved?.playType) {
                          navigate(`/games/${mode.id}/pool?cat=${saved.category}&mode=${mode.id}&play=${saved.playType}&count=10`);
                          return;
                        }
                      } catch (_) { /* ignore */ }
                      navigate(`/games?mode=${mode.id}`);
                    };
                    return (
                      <div
                        key={mode.id}
                        onClick={startGame}
                        className={`group relative rounded-[10px] overflow-hidden border transition-all flex items-center gap-3 p-3 pr-10 cursor-pointer ${
                          isDark
                            ? 'border-slate-600/60 hover:border-slate-500'
                            : 'bg-white border-gray-200 hover:border-brand-300 hover:shadow-[0_10px_24px_-14px_rgba(13,74,138,0.35)]'
                        }`}
                        style={{
                          background: isDark
                            ? 'linear-gradient(160deg, #0d4a8a 0%, #0a3d72 100%)'
                            : undefined,
                          boxShadow: isDark ? '0 10px 28px -16px rgba(4, 19, 37, 0.55)' : undefined,
                          minHeight: 84,
                        }}
                        data-testid={`gamemode-card-${mode.id}`}
                      >
                        {/* Icon box — matches the topic-card title backdrop
                            (`#eef4fb` light / `white/[0.08]` dark). Icon
                            colour is the brand-blue `#1560bd`. */}
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-[10px] flex items-center justify-center ${
                            isDark ? 'bg-white/[0.08]' : 'bg-[#eef4fb]'
                          }`}
                          aria-hidden
                        >
                          <Icon className="w-6 h-6" strokeWidth={2} style={{ color: '#1560bd' }} />
                        </div>

                        {/* Name + Start CTA */}
                        <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold truncate" style={{ color: isDark ? '#ffffff' : '#1560bd' }}>
                              {mode.label}
                            </p>
                            <p className={`text-[11px] truncate ${isDark ? 'text-white/70' : 'text-gray-500'}`}>
                              {mode.blurb}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={startGame}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-[8px] text-[12px] font-semibold transition-colors bg-transparent border ${
                              isDark
                                ? 'text-white border-white/25 hover:border-white/45'
                                : 'border-brand-100 hover:border-brand-200'
                            }`}
                            style={{ color: isDark ? undefined : '#1560bd' }}
                            data-testid={`gamemode-start-${mode.id}`}
                          >
                            <span>Start</span>
                            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.25} />
                          </button>
                        </div>

                        {/* Info `i` icon — top-right, opens instructions.
                            Only revealed on card hover/focus. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setGameInfoMode(mode); }}
                          className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 ${
                            isDark ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'hover:bg-brand-50'
                          }`}
                          style={{ color: isDark ? undefined : '#1560bd' }}
                          aria-label={`${mode.label} instructions`}
                          data-testid={`gamemode-info-${mode.id}`}
                        >
                          <Info className="w-[16px] h-[16px]" strokeWidth={1.75} />
                        </button>
                      </div>
                    );
                  })}
                </div>
                )}

                {/* Active mode header + smart back (steps up: play-type → category → mode → tab) */}
                {activeGameMode && (
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => {
                        if (gamePlayType) { setGamePlayType(null); setLiveSessionsForCat(null); }
                        else if (gameCategory) setGameCategory(null);
                        else setActiveGameMode(null);
                      }}
                      className={`text-[12px] font-medium px-3 py-1.5 rounded-[8px] border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                      data-testid="back-to-modes-btn"
                    >
                      ← Back
                    </button>
                    <span className={`text-[14px] font-medium ${textMain}`} data-testid="active-mode-title">
                      {({'tug-of-war':'Tug of war','knowledge-climb':'Aviator','speed-sprint':'Speed typing','rocket-race':'Rocket race'})[activeGameMode]}
                      {gameCategory && (
                        <span className={`ml-1.5 ${textMuted} text-[12px]`}>
                          · {gameCategory === 'all' ? 'Mix all' : gameCategory.charAt(0).toUpperCase() + gameCategory.slice(1)}
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {/* View 2: Category cards — shown after a mode is picked, before category */}
                {activeGameMode && !gameCategory && (() => {
                  const MODE_THEME = {
                    'tug-of-war':      { color: '#0d4a8a', bg: '#e8f0f9', bgFrom: '#0d4a8a', bgTo: '#1a6db5' },
                    'knowledge-climb': { color: '#3c2e8a', bg: '#f0eefe', bgFrom: '#3c2e8a', bgTo: '#6b4ed6' },
                    'speed-sprint':    { color: '#1a6b3a', bg: '#e8f5ee', bgFrom: '#1a6b3a', bgTo: '#2da75e' },
                    'rocket-race':     { color: '#7a4800', bg: '#fef3e2', bgFrom: '#7a4800', bgTo: '#d6914e' },
                  };
                  const theme = MODE_THEME[activeGameMode] || MODE_THEME['tug-of-war'];
                  // Known category metadata. For any category the backend
                  // returns that ISN'T in this map we still render a chip —
                  // we just fall back to a generic icon + Title-Cased
                  // label. Previously we intersected with this dict, which
                  // silently hid every production category outside the
                  // shortlist (e.g. `kinyarwanda`, `music`, `tech`).
                  const META = {
                    education: { label: 'Education', icon: 'fa-graduation-cap' },
                    driving:   { label: 'Driving',   icon: 'fa-car' },
                    health:    { label: 'Health',    icon: 'fa-heart-pulse' },
                    language:  { label: 'Language',  icon: 'fa-language' },
                    business:  { label: 'Business',  icon: 'fa-briefcase' },
                    custom:    { label: 'Custom',    icon: 'fa-wand-magic-sparkles' },
                  };
                  const titleCase = (s) => String(s || '').trim().replace(/\b\w/g, (c) => c.toUpperCase());
                  const metaFor = (id) => META[id] || { label: titleCase(id), icon: 'fa-folder-open' };

                  const realCats = (gameCategoriesData || []).filter(c => c.id && c.count > 0);
                  const totalQuestions = realCats.reduce((s, c) => s + c.count, 0);

                  // Build the visible category list — every category from
                  // the API (no hard-coded allowlist) plus a "Mix all"
                  // option when there's ≥ 2 distinct categories worth of
                  // content.
                  const visibleCats = realCats.map(c => ({
                    id: c.id,
                    label: metaFor(c.id).label,
                    icon: metaFor(c.id).icon,
                    count: c.count,
                  }));
                  if (realCats.length >= 2) {
                    visibleCats.push({
                      id: 'all',
                      label: 'Mix all',
                      icon: 'fa-shuffle',
                      count: totalQuestions,
                      isMixAll: true,
                    });
                  }

                  // Loading state
                  if (gameCategoriesData === null) {
                    return (
                      <div className={`rounded-[12px] border p-6 text-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`} data-testid="category-grid-loading">
                        <p className={`text-[12px] ${textMuted}`}>Loading categories…</p>
                      </div>
                    );
                  }

                  // Empty state — no playable content anywhere
                  if (visibleCats.length === 0) {
                    return (
                      <div className={`rounded-[12px] border-2 border-dashed p-8 text-center ${isDark ? 'bg-gray-900/40 border-gray-800' : 'bg-[#fafbfc] border-gray-200'}`} data-testid="category-grid-empty">
                        <p className={`text-[14px] font-medium mb-1 ${textMain}`}>No game topics yet</p>
                        <p className={`text-[12px] ${textMuted} mb-4`}>
                          Be the first to publish a public quiz — it'll show up here for everyone to play.
                        </p>
                        <Button
                          size="sm"
                          onClick={() => { navigate('/create'); }}
                          className="bg-brand-500 hover:bg-brand-600 text-white rounded-[10px] px-4 text-[12px] font-medium h-9"
                          data-testid="empty-cats-create-btn"
                        >
                          Create a quiz
                        </Button>
                      </div>
                    );
                  }

                  return (
                    <div data-testid="category-grid">
                      <p className={`text-[12px] mb-3 ${textMuted}`}>Pick a category — questions will be drawn from quizzes and topics in this area.</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {visibleCats.map(c => (
                          <button
                            key={c.id}
                            onClick={() => setGameCategory(c.id)}
                            className="relative text-left rounded-[14px] p-4 overflow-hidden transition-transform active:scale-[0.98]"
                            style={{ background: `linear-gradient(135deg, ${theme.bgFrom} 0%, ${theme.bgTo} 100%)`, minHeight: 110 }}
                            data-testid={`category-card-${c.id}`}
                          >
                            <span className="inline-flex items-center gap-1.5 text-[9px] font-medium tracking-[0.12em] uppercase px-2 py-0.5 rounded-full mb-2" style={{ background: 'rgba(255,255,255,0.18)', color: '#fff' }}>
                              <i className={`fa-solid ${c.icon} text-[10px]`} aria-hidden="true" />
                              {c.isMixAll ? 'Mix all' : c.label}
                            </span>
                            <p className="text-[15px] font-medium leading-[1.2]" style={{ color: '#fff' }}>{c.label}</p>
                            <p className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.78)' }}>
                              {c.isMixAll ? `Random · ${c.count} questions` : `${c.count} question${c.count > 1 ? 's' : ''}`}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* View 3: Play-type cards — Solo / Bot / Others, filtered by mode capability */}
                {activeGameMode && gameCategory && !gamePlayType && (() => {
                  const MODE_CAPS = {
                    'tug-of-war':      ['solo', 'bot', 'others'],
                    'knowledge-climb': ['solo'],
                    'speed-sprint':    ['solo'],
                    'rocket-race':     ['bot', 'others'],
                  };
                  const caps = MODE_CAPS[activeGameMode] || ['solo'];
                  const PLAY = {
                    solo:   { label: 'Play solo',         blurb: 'Just you, vs the clock', tagline: 'SINGLE PLAYER',  bgFrom: '#0d4a8a', bgTo: '#1a6db5' },
                    bot:    { label: 'Play with a bot',   blurb: 'You vs an AI opponent',  tagline: 'VS BOT',          bgFrom: '#3c2e8a', bgTo: '#6b4ed6' },
                    others: { label: 'Play with others',  blurb: 'Live public sessions',   tagline: 'MULTIPLAYER',     bgFrom: '#1a6b3a', bgTo: '#2da75e' },
                  };
                  return (
                    <div data-testid="play-type-grid">
                      <p className={`text-[12px] mb-3 ${textMuted}`}>How do you want to play?</p>
                      <div className={`grid grid-cols-1 ${caps.length === 1 ? '' : caps.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-2`}>
                        {caps.map(p => {
                          const cfg = PLAY[p];
                          return (
                            <button
                              key={p}
                              onClick={() => pickGamePlayType(p)}
                              className="relative text-left rounded-[14px] p-4 overflow-hidden transition-transform active:scale-[0.98]"
                              style={{ background: `linear-gradient(135deg, ${cfg.bgFrom} 0%, ${cfg.bgTo} 100%)`, minHeight: 110 }}
                              data-testid={`play-type-${p}`}
                            >
                              <span className="inline-block text-[9px] font-medium tracking-[0.12em] px-2 py-0.5 rounded-full mb-2" style={{ background: 'rgba(255,255,255,0.18)', color: '#fff' }}>{cfg.tagline}</span>
                              <p className="text-[15px] font-medium leading-[1.2]" style={{ color: '#fff' }}>{cfg.label}</p>
                              <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.78)' }}>{cfg.blurb}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* View 4a: Bot — confirm before launching the game pool.
                    Solo never reaches this screen (it launches immediately
                    from the play-type pick). */}
                {activeGameMode && gameCategory && gamePlayType === 'bot' && (
                  <div className={`rounded-[12px] border p-5 text-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`} data-testid="launch-pool-card">
                    <p className={`text-[14px] font-medium mb-1 ${textMain}`}>Ready to play</p>
                    <p className={`text-[12px] ${textMuted} mb-4`}>
                      You vs an AI opponent · up to 10 random questions from{' '}
                      <span className="font-medium">{gameCategory === 'all' ? 'all categories' : gameCategory}</span>
                    </p>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/games/${activeGameMode}/pool?cat=${gameCategory}&mode=${activeGameMode}&play=bot&count=10`)}
                      className="bg-brand-500 hover:bg-brand-600 text-white rounded-[10px] px-6 text-[13px] font-medium h-9"
                      data-testid="start-pool-btn"
                    >
                      Start →
                    </Button>
                  </div>
                )}

                {/* View 4b: Others — list active live sessions for this category, with empty state */}
                {activeGameMode && gameCategory && gamePlayType === 'others' && (() => {
                  // Lazy fetch sessions on first render of this view
                  if (liveSessionsForCat === null) {
                    (async () => {
                      try {
                        const res = await axios.get(`${API}/live-sessions/active`, {
                          params: { category: gameCategory, mode: activeGameMode, limit: 12 },
                        });
                        setLiveSessionsForCat(Array.isArray(res.data) ? res.data : []);
                      } catch {
                        setLiveSessionsForCat([]);
                      }
                    })();
                  }
                  const sessions = liveSessionsForCat || [];
                  return (
                    <div data-testid="others-view">
                      {liveSessionsForCat === null ? (
                        <div className={`rounded-[12px] border p-6 text-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                          <p className={`text-[12px] ${textMuted}`}>Looking for live sessions…</p>
                        </div>
                      ) : sessions.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {sessions.map((s, idx) => {
                            const goJoin = () => {
                              if (s.session_id) navigate(`/live/${s.session_id}`);
                              else if (isAuthenticated) navigate('/dashboard');
                              else openAuthWithRedirect('/');
                            };
                            return (
                              <LiveGameCard
                                key={s.session_id || idx}
                                status={s.status || 'live'}
                                title={s.session_name || 'Live game'}
                                subtitle={`${(s.host_name || 'Host')} · ${s.time_ago || 'just now'}`}
                                room_code={s.room_code || (s.session_id || '').slice(0, 6).toUpperCase()}
                                player_count={s.player_count || 0}
                                max_players={s.max_players || 30}
                                question_progress={s.question_progress}
                                starts_in={s.starts_in}
                                top3={(s.players || []).slice(0, 3).map(p => ({ name: p.name, score: p.score || 0 }))}
                                isDark={isDark}
                                onJoin={goJoin}
                                onSeeResults={goJoin}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <div className={`rounded-[12px] border-2 border-dashed p-8 text-center ${isDark ? 'bg-gray-900/40 border-gray-800' : 'bg-[#fafbfc] border-gray-200'}`} data-testid="others-empty">
                          <p className={`text-[14px] font-medium mb-1 ${textMain}`}>No one's hosting yet</p>
                          <p className={`text-[12px] ${textMuted} mb-4`}>
                            Be the first to start a {gameCategory === 'all' ? 'mixed' : gameCategory} {({'tug-of-war':'tug of war','knowledge-climb':'aviator','speed-sprint':'speed typing','rocket-race':'rocket race'})[activeGameMode]} session — invite friends with the link.
                          </p>
                          <div className="flex justify-center gap-2 flex-wrap">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/games/${activeGameMode}/pool?cat=${gameCategory}&mode=${activeGameMode}&play=bot&count=10`)}
                              className="bg-brand-500 hover:bg-brand-600 text-white rounded-[10px] px-4 text-[12px] font-medium h-9"
                              data-testid="others-fallback-bot-btn"
                            >
                              Play with a bot instead
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/games/${activeGameMode}/pool?cat=${gameCategory}&mode=${activeGameMode}&play=others`)}
                              className={`rounded-[10px] px-4 text-[12px] font-medium h-9 border ${isDark ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-700'}`}
                              data-testid="others-host-cta"
                            >
                              Host a session
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* View All — only show when in a topic picker */}
                {activeGameMode && (
                  <div className="flex justify-center mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setActiveGameMode(null); setGameCategory(null); setGamePlayType(null); setLiveSessionsForCat(null); }}
                      className={`rounded-[10px] px-6 text-[13px] font-medium border ${isDark ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-600'}`}
                      data-testid="view-all-games-btn"
                    >
                      ← Back to all games
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
          </div>{/* end tabs card */}

          {/* Build Your Own — single CTA under the topics card. The
              prior three-pill layout (Quick quiz / Live quiz / Course)
              all landed on the same /create page, so a single button
              with clarifying subtext is faster to scan. */}
          <div className={`flex flex-col sm:flex-row items-center gap-3 sm:gap-4 p-[14px] px-4 mt-4 mb-[14px] rounded-[12px] border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="w-[38px] h-[38px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: '#E6F1FB' }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M9 3v12M3 9h12" stroke="#185FA5" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <p className={`text-[13px] font-medium ${textMain}`}>{getText('build_your_own_title', 'Build your own')}</p>
              <p className={`text-[12px] ${textMuted}`}>{getText('build_your_own_subtitle', 'Create a quiz or game from scratch or with AI')}</p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/create')}
              data-testid="build-your-own-btn"
              className="bg-brand-500 hover:bg-brand-600 text-white rounded-[8px] px-4 text-[12px] font-medium h-8 flex-shrink-0"
            >
              {getText('build_your_own_cta', 'Get started')}
            </Button>
          </div>

          {/* Adverts / Campaigns banner section — driven by the
              `announcements` admin CMS. Renders every active banner that
              has an `image_url`; this section is intentionally NOT
              dismissible (it carries campaign / advert content that must
              stay visible). Layout: image first (full-width on mobile,
              fixed `220px` rail on desktop), copy + CTA centred under it
              on mobile / right of it on desktop. Image dimensions are
              fixed so the banner reads consistently across screens. */}
          {(landingBanners || []).filter(b => b.image_url && b.active !== false).length > 0 && (
            <div className="mt-6 mb-6 space-y-4" data-testid="landing-banners">
              {(landingBanners || []).filter(b => b.image_url && b.active !== false).map((banner) => (
                <div
                  key={banner.announcement_id}
                  className={`relative rounded-[14px] border overflow-hidden flex flex-col sm:flex-row sm:items-stretch ${
                    isDark ? 'border-slate-800' : 'border-gray-200'
                  }`}
                  style={{
                    // Mostly-white card so it clearly separates from the
                    // landing's `#f5f7fa` page background, with just a
                    // hint of brand tint in the top-left corner so the
                    // decorative pattern stack has something to layer on.
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(13, 74, 138, 0.20) 0%, rgba(34, 211, 238, 0.06) 100%), #0b1220'
                      : 'linear-gradient(135deg, #f4f8fc 0%, #ffffff 45%, #ffffff 100%)',
                  }}
                  data-testid={`landing-banner-${banner.announcement_id}`}
                >
                  {/* Fancy background decorations (dot grid, diagonal
                   * stripes, concentric rings, wave, and cyan blob glow)
                   * removed per product request. The card now uses only
                   * the base card background set on the parent. */}

                  {/* Image rail — inset with the same padding as the copy
                      column on desktop so the photo carries the section's
                      breathing room. On mobile the photo gets the same
                      inset all-around so the rounded image sits neatly
                      inside the patterned backdrop. */}
                  <div className="relative z-10 p-5 sm:p-6 sm:pr-3 flex-shrink-0">
                    <div className="relative w-full sm:w-[220px] sm:h-[180px] aspect-[16/9] sm:aspect-auto overflow-hidden rounded-[12px] shadow-sm">
                      <img
                        src={banner.image_url}
                        alt={banner.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Copy + CTA — centred on mobile, left-aligned on desktop.
                      Generous padding so the section breathes against
                      neighbouring landing cards. */}
                  <div className="relative z-10 flex-1 min-w-0 p-5 pt-1 sm:p-7 sm:pl-3 flex flex-col gap-3 items-center text-center sm:items-start sm:text-left">
                    {banner.badge_text && (
                      <span
                        className="text-[11px] font-semibold px-2.5 py-1 rounded-[8px] border"
                        style={{
                          background: isDark ? 'rgba(74, 222, 128, 0.15)' : '#dcfce7',
                          color: isDark ? '#86efac' : '#15803d',
                          borderColor: isDark ? 'rgba(74, 222, 128, 0.35)' : '#bbf7d0',
                        }}
                      >
                        {banner.badge_text}
                      </span>
                    )}
                    <h3
                      className={`text-[20px] sm:text-[22px] font-bold leading-tight ${isDark ? 'text-white' : ''}`}
                      style={!isDark ? { color: '#1560bd' } : undefined}
                    >
                      {banner.title}
                    </h3>
                    {banner.message && (
                      <p className={`text-[13px] leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {banner.message}
                      </p>
                    )}
                    {banner.link_url && (
                      <a
                        href={banner.link_url}
                        rel="noopener noreferrer"
                        className={`mt-1 inline-flex items-center justify-center px-4 py-2 rounded-[8px] text-[13px] font-semibold ${
                          isDark
                            ? 'bg-white/10 text-white hover:bg-white/15'
                            : 'bg-white text-[#1560bd] hover:bg-[#eef4fb] border border-brand-100'
                        }`}
                        data-testid={`banner-cta-${banner.announcement_id}`}
                      >
                        {banner.link_text || 'Learn more'}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Leaderboard Strip — single merged ranking combining course +
              quick-quiz activity for the week. */}
          {(() => {
            const leaders = topLearners || [];
            const rankColors = ['#854F0B', '#5F5E5A', '#993C1D'];
            // Brand-blue palette for all avatars — varies by depth so
            // adjacent rows have a subtle tonal difference without
            // breaking the unified theme.
            const avatarColors = ['#0d4a8a', '#1a6db5', '#3083d1', '#5099d9', '#75b0e0'];
            return (
              <div className="mb-[14px]" data-testid="leaderboard-week">
                <div className={`rounded-[12px] border p-[14px] px-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                  {/* Heading lives inside the card now — no uppercase. The
                      "Full leaderboard" link sits on its right for parity
                      with the previous external header. */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[13px] font-medium ${textMain}`}>
                      This week&apos;s top learners
                    </span>
                    {leaders.length > 0 && (
                      <button
                        onClick={() => navigate('/leaderboard')}
                        className="text-[12px] font-medium text-brand-500"
                        data-testid="leaderboard-full-link"
                      >
                        Full leaderboard
                      </button>
                    )}
                  </div>
                  {leaders.length === 0 && (
                    <p className={`text-[12px] text-center py-2 ${textMuted}`} data-testid="leaderboard-empty">
                      No top learners yet this week. Take a quick quiz or finish a topic test to climb the board.
                    </p>
                  )}
                  {leaders.slice(0, 5).map((u, i) => (
                    <div
                      key={`leader-${i}`}
                      className={`flex items-center gap-[10px] py-2 ${i < leaders.length - 1 && i < 4 ? `border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}` : ''}`}
                    >
                      <span className="w-5 text-[13px] font-semibold text-center flex-shrink-0" style={{ color: i < 3 ? rankColors[i] : (isDark ? '#6b7280' : '#9ca3af') }}>
                        {i + 1}
                      </span>
                      <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0" style={{ background: avatarColors[i % avatarColors.length] }}>
                        {(u.name || '?').substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`text-[13px] font-medium truncate ${textMain}`}>{u.name}</span>
                        {u.streak > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium flex-shrink-0">{u.streak} days</span>
                        )}
                      </div>
                      <span className={`text-[13px] font-medium flex-shrink-0 ${textMuted}`}>{(u.points || 0).toLocaleString()} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Quick Stats - Minimal */}
          {isLandingVisible('stats') && (
            <motion.div
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center gap-8 py-2"
            >
              {(siteContent.stats || [
                { icon: 'Users', value: '5k+', label: 'Students' },
                { icon: 'Target', value: '95%', label: 'Pass rate' },
                { icon: 'Zap', value: '10k+', label: 'Questions' }
              ]).map((stat, idx) => {
                const StatIcon = getIcon(stat.icon);
                return (
                  <div key={idx} className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <StatIcon className={`w-3 h-3 ${textMuted}`} />
                      <span className={`font-medium text-sm ${textMain}`}>{stat.value}</span>
                    </div>
                    <p className={`text-[10px] ${textMuted}`}>{stat.label}</p>
                  </div>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>
      )}

      {/* Gradient Separator */}
      <div className="max-w-4xl mx-auto px-8 my-3"><div className={`h-px ${isDark ? 'bg-gradient-to-r from-transparent via-brand-700 to-transparent' : 'bg-gradient-to-r from-transparent via-brand-300 to-transparent'}`} /></div>

      {/* Topics Grid - when hero is hidden, show topics directly */}
      {!isLandingVisible('hero') && (
        <section className={`pt-20 pb-4 px-4 ${isDark ? '' : ''}`}>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">
              {filteredTopics
                // Respect admin-assigned order from the backend
                .map((topic) => {
                const isComingSoon = topic.status === 'coming_soon';
                return (
                  <div
                    key={topic.topic_id}
                    className={`group relative overflow-hidden rounded-2xl transition-all ${
                      isDark 
                        ? 'border border-slate-600/50 hover:border-slate-500/70' 
                        : 'bg-white border border-gray-200'
                    } ${isComingSoon ? 'cursor-default' : 'cursor-pointer hover:shadow-xl'}`}
                    style={{
                      background: isDark
                        ? 'linear-gradient(155deg, #2a3a52 0%, #1a2436 100%)'
                        : undefined,
                      boxShadow: isDark && !isComingSoon
                        ? '0 16px 40px -18px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(148, 163, 184, 0.06), inset 0 1px 0 rgba(148, 163, 184, 0.12)'
                        : undefined,
                    }}
                    onClick={() => {
                      if (isComingSoon) {
                        setComingSoonTapped(topic.topic_id);
                        setTimeout(() => setComingSoonTapped(null), 2000);
                      } else {
                        handleTopicSelect(topic);
                      }
                    }}
                    data-testid={`topic-card-${topic.topic_id}`}
                  >
                    {/* Decorative dark-mode glow shapes */}
                    {isDark && !isComingSoon && (
                      <>
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -top-16 -right-12 w-40 h-40 rounded-full blur-3xl opacity-40 z-10"
                          style={{ background: 'radial-gradient(circle, rgba(34, 211, 238, 0.35) 0%, transparent 70%)' }}
                        />
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -bottom-16 -left-12 w-44 h-44 rounded-full blur-3xl opacity-30 z-10"
                          style={{ background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 70%)' }}
                        />
                      </>
                    )}
                    <TopicThumbnailCard topic={topic} isComingSoon={isComingSoon} />
                    {isComingSoon && comingSoonTapped === topic.topic_id && (
                      <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 rounded-2xl">
                        <div className="flex flex-col items-center gap-2">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                            <Lock className="w-6 h-6 text-brand-500" />
                          </div>
                          <span className="text-white text-sm font-medium">Coming Soon</span>
                        </div>
                      </div>
                    )}
                    <div className="relative p-4 z-20">
                      <div
                        className={`text-[10px] font-semibold uppercase tracking-[0.18em] mb-1.5 ${
                          isDark ? 'text-slate-400' : 'text-brand-500'
                        }`}
                      >
                        {(topic.category || 'Course').replace(/^./, c => c.toUpperCase())}
                      </div>
                      <h3 className={`text-base font-semibold mb-1 ${isDark ? 'text-white' : textMain}`}>{getTranslatedContent(topic, 'name')}</h3>
                      {topic.description && <p className={`text-xs font-light line-clamp-2 ${isDark ? 'text-slate-400' : textMuted}`}>{getTranslatedContent(topic, 'description')}</p>}
                    </div>
                    {!isComingSoon && (
                      <div
                        className={`relative z-20 px-4 py-2.5 flex items-center justify-between ${
                          isDark ? '' : `border-t border-gray-100`
                        }`}
                        style={
                          isDark
                            ? {
                                background: 'linear-gradient(90deg, rgba(34, 211, 238, 0.18) 0%, rgba(37, 99, 235, 0.32) 100%)',
                                borderTop: '1px solid rgba(34, 211, 238, 0.18)',
                              }
                            : undefined
                        }
                      >
                        <span className={`text-xs ${isDark ? 'text-slate-300' : textMuted}`}>{(topic.available_languages || ['en']).map(c => SUPPORTED_LANGUAGES.find(l => l.code === c)?.name || c).join(', ')}</span>
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 ${
                            isDark
                              ? 'text-white shadow-md shadow-blue-900/40'
                              : `font-medium ${textMuted}`
                          }`}
                          style={
                            isDark
                              ? { background: 'linear-gradient(90deg, #22d3ee 0%, #2563eb 100%)' }
                              : undefined
                          }
                        >
                          Start <ArrowRight className="w-3 h-3 inline" />
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Features - Scroll Animated with Zoom */}
      {isLandingVisible('features') && (
        <motion.section 
          initial={false}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className={`py-6 sm:py-8 px-4 hidden md:block ${isDark ? 'bg-gray-900/50' : ''}`}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center items-start gap-10 lg:gap-14">
              {(siteContent.features || [
                { icon: 'Target', title: 'Adaptive', description: 'AI finds your weak spots' },
                { icon: 'Clock', title: 'Flexible', description: 'Timed or untimed modes' },
                { icon: 'Trophy', title: 'Track Progress', description: 'See your improvement' }
              ]).map((feature, idx) => {
                const FeatureIcon = getIcon(feature.icon);
                return (
                  <motion.div
                    key={idx}
                    initial={false}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.12, duration: 0.5 }}
                    className="text-center"
                  >
                    <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-brand-500/15' : 'bg-brand-50'} flex items-center justify-center mx-auto mb-2.5`}>
                      <FeatureIcon className="w-5 h-5 text-brand-500" />
                    </div>
                    <h3 className={`font-medium text-sm mb-0.5 ${textMain}`}>{feature.title}</h3>
                    <p className={`text-[11px] ${textMuted} font-light`}>{feature.description || feature.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>
      )}

      {/* Pricing - Gradient Card with Zoom effect */}
      {isLandingVisible('pricing') && (
        <section className="py-6 sm:py-10 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="relative overflow-hidden rounded-3xl"
            >
              {/* Background - adapts to theme */}
              <div className={`absolute inset-0 ${
                isDark 
                  ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800' 
                  : 'bg-gradient-to-br from-[#0d4a8a] via-[#1560bd] to-[#0a3d6e]'
              }`} />
              <div className={`absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 ${
                isDark ? 'bg-brand-500/10' : 'bg-white/10'
              }`} />
              <div className={`absolute bottom-0 left-0 w-72 h-72 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 ${
                isDark ? 'bg-purple-500/10' : 'bg-brand-400/20'
              }`} />
              <div className={`absolute top-1/2 left-1/2 w-64 h-64 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2 ${
                isDark ? 'bg-brand-400/5' : 'bg-cyan-400/10'
              }`} />
              
              <div className="relative z-10 p-6 sm:p-10 md:p-12">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-4 backdrop-blur-sm ${
                    isDark 
                      ? 'bg-brand-500/20 text-brand-400' 
                      : 'bg-white/15 text-white'
                  }`}>
                    <Zap className="w-4 h-4" />
                    {siteContent.pricing_badge || 'No subscriptions'}
                  </div>
                  <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
                    {siteContent.pricing_title || 'Choose Your Credits'}
                  </h2>
                  <p className={`text-sm max-w-md mx-auto ${isDark ? 'text-gray-400' : 'text-white/70'}`}>
                    {siteContent.pricing_subtitle || 'Pay once, use anytime. Credits never expire.'}
                  </p>
                </div>

                {/* Credit Packages - Main Focus */}
                {packages.length > 0 && (
                  <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    {packages.slice(0, 3).map((pkg, idx) => (
                      <motion.button
                        key={pkg.package_id}
                        initial={false}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 + idx * 0.1, duration: 0.4 }}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => {
                          if (isAuthenticated) {
                            navigate('/credits');
                          } else {
                            setShowAuth(true);
                          }
                        }}
                        className={`relative p-5 rounded-2xl text-left transition-all ${
                          pkg.is_featured 
                            ? isDark
                              ? 'bg-brand-500 text-white'
                              : 'bg-white text-gray-900'
                            : isDark
                              ? 'bg-gray-800/80 backdrop-blur-sm text-white border border-gray-700 hover:bg-gray-800'
                              : 'bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/15'
                        }`}
                        data-testid={`landing-pkg-${pkg.package_id}`}
                      >
                        {pkg.is_featured && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className={`text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 ${
                              isDark 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-amber-500 text-white'
                            }`}>
                              <Star className="w-3 h-3 fill-white" />
                              Popular
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className={`text-3xl font-bold ${
                            pkg.is_featured 
                              ? isDark ? 'text-white' : 'text-brand-600'
                              : ''
                          }`}>{pkg.credits}</span>
                          <span className={`text-sm ${
                            pkg.is_featured 
                              ? isDark ? 'text-white/70' : 'text-gray-500'
                              : isDark ? 'text-gray-400' : 'text-white/60'
                          }`}>credits</span>
                        </div>
                        
                        <div className={`text-lg font-semibold mb-3 ${
                          pkg.is_featured 
                            ? isDark ? 'text-white' : 'text-gray-900'
                            : ''
                        }`}>
                          {(pkg.price_rwf || 0).toLocaleString()} RWF
                        </div>
                        
                        <div className={`text-xs ${
                          pkg.is_featured 
                            ? isDark ? 'text-white/70' : 'text-gray-500'
                            : isDark ? 'text-gray-500' : 'text-white/60'
                        }`}>
                          {pkg.credits >= 600 ? 'Best value' : pkg.credits >= 250 ? 'Most popular' : 'Great start'}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Benefits Row */}
                <div className="flex flex-wrap justify-center gap-6 mb-8">
                  {[
                    { icon: CheckCircle, text: '20 free credits on signup' },
                    { icon: Clock, text: 'Credits never expire' },
                    { icon: Shield, text: 'Secure IremboPay checkout' },
                  ].map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
                      <item.icon className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-green-400'}`} />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="text-center">
                  <Button
                    onClick={() => {
                      if (isAuthenticated) {
                        navigate('/credits');
                      } else {
                        setShowAuth(true);
                      }
                    }}
                    size="lg"
                    className={`rounded-[10px] font-semibold px-6 sm:px-8 gap-2 text-sm sm:text-base max-w-full ${
                      isDark 
                        ? 'bg-brand-500 hover:bg-brand-600 text-white' 
                        : 'bg-white hover:bg-gray-100 text-brand-600'
                    }`}
                  >
                    {isAuthenticated ? 'Buy Credits' : (siteContent.pricing_cta || 'Get Started Free')}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Resources Section - Articles with Zoom Animation */}
      {isLandingVisible('articles') && articles.length > 0 && (
        <motion.section 
          initial={false}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className={`py-6 sm:py-8 px-4 ${isDark ? 'bg-gray-900/30' : ''}`}
        >
          <div className="max-w-5xl mx-auto">
            <motion.div 
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="flex items-center justify-between mb-4 sm:mb-6"
            >
              <div>
                <h2 className="text-lg font-medium mb-0.5">{siteContent.articles_title || 'Learn More'}</h2>
                <p className={`text-[12px] ${textMuted} font-light`}>{siteContent.articles_subtitle || 'Helpful guides to get you started'}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="rounded-[10px] gap-1.5 hidden md:flex text-[12px] font-normal"
                onClick={() => navigate('/articles')}
              >
                View All
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </motion.div>
          
          <div className="grid md:grid-cols-3 gap-3">
            {articles.slice(0, 3).map((article, idx) => (
              <motion.div
                key={article.article_id}
                onClick={() => {
                  if (article.external_url) {
                    window.open(article.external_url, '_blank');
                  } else {
                    navigate(`/articles/${article.article_id}`);
                  }
                }}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.4 }}
                whileHover={{ y: -3 }}
                className={`group p-4 rounded-xl ${
                  isDark 
                    ? 'bg-gray-800/50 border border-gray-700/50 hover:border-brand-500/40' 
                    : 'bg-white/70 backdrop-blur-sm border border-gray-200/60 hover:border-brand-300/50 shadow-sm hover:shadow-md'
                } transition-all cursor-pointer`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-brand-500/15 text-brand-400' : 'bg-brand-50 text-brand-600'} font-light`}>
                    {article.category}
                  </span>
                  <span className={`text-[10px] ${textMuted} font-light`}>{article.read_time}</span>
                  {article.external_url && (
                    <span className={`text-[10px] ${textMuted}`}>
                      <ArrowRight className="w-2.5 h-2.5 rotate-[-45deg]" />
                    </span>
                  )}
                </div>
                <h3 className="font-medium text-[13px] mb-1.5 group-hover:text-brand-500 transition-colors line-clamp-2 leading-snug">
                  {article.title}
                </h3>
                <p className={`text-[11px] ${textMuted} line-clamp-2 font-light leading-relaxed`}>
                  {article.description}
                </p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-5 text-center md:hidden">
            <Button 
              variant="outline" 
              size="sm"
              className="rounded-[10px] gap-1.5 text-[12px] font-normal"
              onClick={() => navigate('/articles')}
            >
              View All Articles
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </motion.section>
      )}

      {/* Contact Info & Social - Scroll Animated */}
      {isLandingVisible('contact') && (
        <motion.section 
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className={`py-4 sm:py-6 px-4`}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Contact Info */}
              <div className="flex flex-wrap justify-center md:justify-start gap-4 sm:gap-5">
                <div className="flex items-center gap-1.5">
                  <Mail className={`w-3.5 h-3.5 ${textMuted}`} />
                  <span className={`text-[12px] ${textMuted} font-light`}>{siteContent.contact_email || 'support@ikizamini.com'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Phone className={`w-3.5 h-3.5 ${textMuted}`} />
                  <span className={`text-[12px] ${textMuted} font-light`}>{siteContent.contact_phone || '+250 788 000 000'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className={`w-3.5 h-3.5 ${textMuted}`} />
                  <span className={`text-[12px] ${textMuted} font-light`}>{siteContent.contact_address || 'Kigali, Rwanda'}</span>
                </div>
              </div>
            
            {/* Social Media */}
            <div className="flex gap-1">
              {[
                { name: 'Facebook', url: siteContent.social_links?.facebook || '#', icon: (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                )},
                { name: 'Twitter', url: siteContent.social_links?.twitter || '#', icon: (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                )},
                { name: 'Instagram', url: siteContent.social_links?.instagram || '#', icon: (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                )},
                { name: 'WhatsApp', url: siteContent.social_links?.whatsapp || '#', icon: (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                )}
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-1.5 rounded-full ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors`}
                  aria-label={social.name}
                >
                  <span className={textMuted}>{social.icon}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </motion.section>
      )}

      {/* Footer - Minimal & Sleek */}
      <footer className={`py-4 px-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <span className={`text-[13px] font-medium ${textMain}`}>{siteContent.site_name || 'Ikizamini'}</span>
          <p className={`text-[11px] ${textMuted} font-light`}>© {new Date().getFullYear()}</p>
        </div>
      </footer>

      {/* Quick Quiz Overlay */}
      {activeQuickQuiz && (
        <QuickQuizPlayer quiz={activeQuickQuiz} isDark={isDark} onClose={(resultData) => {
          if (resultData && activeQuickQuiz.slug) {
            markQuizTaken(activeQuickQuiz.slug, resultData.playerName, resultData.score);
          }
          setActiveQuickQuiz(null);
        }} />
      )}

      {/* Quiz Leaderboard Modal */}
      {quizLeaderboard && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setQuizLeaderboard(null)} />
          <div className={`relative w-full max-w-md mx-4 rounded-[16px] overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
            <div className="flex items-center justify-between px-5 py-3.5 bg-[#0d4a8a] text-white">
              <span className="text-[14px] font-medium">{quizLeaderboard.title} — Leaderboard</span>
              <button onClick={() => setQuizLeaderboard(null)} className="text-white/70 text-[22px] leading-none">&times;</button>
            </div>
            <div className="p-5">
              {quizLeaderboard.leaders.length === 0 ? (
                <p className={`text-center text-[13px] ${textMuted} py-6`}>No attempts yet</p>
              ) : (
                <div className={`rounded-[10px] border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                  {quizLeaderboard.leaders.slice(0, 10).map((p, i) => {
                    const rankColors = ['#854F0B','#5F5E5A','#993C1D'];
                    const avatarColors = ['#0d4a8a', '#1a6db5', '#3083d1', '#5099d9', '#75b0e0'];
                    const taken = getTakenQuizzes()[quizLeaderboard.slug];
                    const isYou = taken && p.name === taken.name;
                    return (
                      <div key={i} className={`flex items-center gap-2.5 px-3 py-2.5 ${i < quizLeaderboard.leaders.length - 1 ? `border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}` : ''} ${isYou ? (isDark ? 'bg-brand-900/20' : 'bg-brand-50/50') : ''}`}>
                        <span className="w-5 text-[12px] font-semibold text-center" style={{ color: i < 3 ? rankColors[i] : '#9ca3af' }}>{i + 1}</span>
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold text-white" style={{ background: avatarColors[i % avatarColors.length] }}>
                          {(p.name || '?').substring(0, 2).toUpperCase()}
                        </div>
                        <span className={`text-[13px] flex-1 truncate ${isYou ? 'font-medium' : ''} ${textMain}`}>{p.name}{isYou ? ' (you)' : ''}</span>
                        <span className={`text-[13px] font-medium ${textMuted}`}>{p.score}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button onClick={() => { setActiveQuickQuiz(quickQuizzes.find(q => q.slug === quizLeaderboard.slug)); setQuizLeaderboard(null); }}
                className="w-full mt-4 bg-brand-500 hover:bg-brand-600 text-white rounded-[12px] text-[13px] font-medium">
                Take this quiz
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Topic Detail Modal */}
      <AnimatePresence>
        {selectedTopic && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedTopic(null)}
          >
            <motion.div
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-lg p-6 rounded-2xl ${isDark ? 'bg-gray-900' : 'bg-white'} shadow-2xl max-h-[85vh] overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start gap-4 mb-5">
                <div className={`p-3 rounded-xl ${isDark ? 'bg-brand-500/20' : 'bg-brand-100'} flex-shrink-0`}>
                  <BookOpen className="w-6 h-6 text-brand-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold mb-1">{selectedTopic.name}</h3>
                  <p className={`text-sm ${textMuted} line-clamp-2`}>{selectedTopic.description}</p>
                </div>
                <button 
                  onClick={() => setSelectedTopic(null)}
                  className={`p-2 rounded-full ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors flex-shrink-0`}
                >
                  <svg className={`w-5 h-5 ${textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Quick Stats */}
              <div className={`grid grid-cols-3 gap-3 p-4 rounded-xl mb-5 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <div className="text-center">
                  <p className="text-2xl font-bold text-brand-500">{selectedTopicSubtopics.length || '—'}</p>
                  <p className={`text-xs ${textMuted}`}>Subtopics</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-brand-500">{selectedTopic.questions_per_test || 10}</p>
                  <p className={`text-xs ${textMuted}`}>Questions/Test</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-brand-500">{selectedTopic.time_limit_minutes || 30}</p>
                  <p className={`text-xs ${textMuted}`}>Minutes</p>
                </div>
              </div>

              {/* Subtopics List */}
              <div className="mb-5">
                <h4 className={`text-xs font-medium ${textMuted} uppercase tracking-wide mb-3 flex items-center gap-2`}>
                  <Target className="w-3.5 h-3.5" />
                  Topics Covered
                </h4>
                {loadingSubtopics ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : selectedTopicSubtopics.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                    {selectedTopicSubtopics.slice(0, 8).map((subtopic, idx) => (
                      <div 
                        key={subtopic.subtopic_id || idx}
                        className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${isDark ? 'bg-brand-500/20 text-brand-400' : 'bg-brand-100 text-brand-600'}`}>
                          {idx + 1}
                        </div>
                        <span className="text-sm font-medium flex-1 truncate">{subtopic.name}</span>
                        <span className={`text-xs ${textMuted}`}>{subtopic.question_count || '?'} Qs</span>
                      </div>
                    ))}
                    {selectedTopicSubtopics.length > 8 && (
                      <p className={`text-xs ${textMuted} text-center pt-2`}>
                        +{selectedTopicSubtopics.length - 8} more subtopics
                      </p>
                    )}
                  </div>
                ) : (
                  <p className={`text-sm ${textMuted} text-center py-4`}>No subtopics available</p>
                )}
              </div>

              {/* Features */}
              <div className={`flex items-center gap-4 p-3 rounded-xl mb-5 ${isDark ? 'bg-gray-800/30' : 'bg-gray-50/50'}`}>
                {selectedTopic.has_certificate !== false && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <Award className="w-4 h-4 text-emerald-500" />
                    <span className={textMuted}>Certificate at {selectedTopic.certificate_threshold || 70}%</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-xs">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  <span className={textMuted}>Progress tracking</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => { setSelectedTopic(null); startGuestTest(selectedTopic.topic_id); }}
                  disabled={guestTestStarted}
                  className="flex-1 rounded-full bg-brand-600 hover:bg-brand-700 text-white gap-2"
                  data-testid={`try-free-${selectedTopic.topic_id}`}
                >
                  <Play className="w-4 h-4" />
                  Try Free Demo
                </Button>
                <Button
                  onClick={() => { 
                    // Store the topic for redirect after login
                    localStorage.setItem('ikizamini_pending_topic', selectedTopic.topic_id);
                    setSelectedTopic(null); 
                    setShowAuth(true); 
                  }}
                  variant="outline"
                  className="rounded-full"
                >
                  {siteContent.hero_cta || 'Sign In'}
                </Button>
              </div>
              
              <p className={`text-center text-xs ${textMuted} mt-4`}>
                {siteContent.topic_modal_demo_note || 'Demo includes 5 sample questions'}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Verify Certificate Modal */}
      <AnimatePresence>
        {showVerifyModal && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4"
            onClick={() => setShowVerifyModal(false)}
          >
            <motion.div
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`${cardBg} rounded-2xl p-6 max-w-md w-full shadow-xl border ${borderColor}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-brand-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-brand-500" />
                </div>
                <h3 className={`text-xl font-bold ${textMain}`}>Verify Certificate</h3>
                <p className={`${textMuted} text-sm mt-1`}>Enter certificate ID to verify authenticity</p>
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  value={verifyCertId}
                  onChange={(e) => setVerifyCertId(e.target.value)}
                  placeholder="e.g., 12345678"
                  className={`w-full px-4 py-3 rounded-lg border ${borderColor} ${cardBg} ${textMain} text-sm focus:outline-none focus:ring-2 focus:ring-brand-500`}
                  data-testid="verify-cert-input"
                  maxLength={8}
                />
                
                <Button
                  onClick={() => {
                    if (verifyCertId.trim()) {
                      navigate(`/verify/${verifyCertId.trim()}`);
                      setShowVerifyModal(false);
                    } else {
                      toast.error('Please enter a certificate ID');
                    }
                  }}
                  className="w-full rounded-full bg-brand-500 hover:bg-brand-600 text-white"
                  data-testid="verify-cert-submit"
                >
                  Verify Certificate
                </Button>
                
                <button
                  onClick={() => setShowVerifyModal(false)}
                  className={`w-full py-2 text-sm ${textMuted} hover:text-brand-500`}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Topic Info Modal — triggered by the `i` icon on each topic card.
          Surfaces the full set of secondary details that we deliberately
          kept off the card itself: full description, subtopic + quiz
          counts, certificate status, language coverage, mastery %. */}
      <AnimatePresence>
        {infoTopic && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
            onClick={() => setInfoTopic(null)}
            data-testid="topic-info-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className={`relative w-full max-w-md rounded-[12px] overflow-hidden border ${
                isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header — brand blue strip with title + close */}
              <div
                className="p-5 flex items-start justify-between gap-3"
                style={{ background: 'linear-gradient(160deg, #0d4a8a 0%, #0a3d72 100%)' }}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70 mb-1">
                    {(infoTopic.category || 'Course').replace(/^./, c => c.toUpperCase())}
                  </div>
                  <h3 className="text-[18px] font-semibold text-white leading-tight">
                    {getTranslatedContent(infoTopic, 'name')}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setInfoTopic(null)}
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white/85 hover:bg-white/15 hover:text-white transition-colors"
                  aria-label="Close"
                  data-testid="topic-info-close"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4">
                {getTranslatedContent(infoTopic, 'description') && (
                  <p className={`text-[13px] leading-relaxed ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {getTranslatedContent(infoTopic, 'description')}
                  </p>
                )}

                {/* Detail grid */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Subtopics', value: infoTopic.total_subtopics ?? infoTopic.subtopic_count ?? 0 },
                    { label: 'Quizzes', value: infoTopic.static_quizzes_count ?? infoTopic.quizzes_count ?? 0 },
                    { label: 'Certificate', value: infoTopic.has_certificate !== false ? 'Included' : 'No' },
                    { label: 'Languages', value: (infoTopic.available_languages || ['en']).map(c => SUPPORTED_LANGUAGES.find(l => l.code === c)?.name || c).join(', ') || 'English' },
                    // Cost — show "Free" when the topic's effective cost
                    // is 0, otherwise just "Paid" (no number). Source of
                    // truth is `cost` from `/topics/with-credits` which
                    // already resolves use_custom_credit_cost vs the
                    // global pricing rule.
                    { label: 'Cost', value: (infoTopic.cost == null || infoTopic.cost === 0) ? 'Free' : 'Paid' },
                    ...((infoTopic.mastery_percentage ?? infoTopic.progress ?? 0) > 0
                      ? [{ label: 'Mastery', value: `${Math.round(infoTopic.mastery_percentage ?? infoTopic.progress)}%` }]
                      : []),
                  ].map((row) => (
                    <div
                      key={row.label}
                      className={`rounded-[8px] px-3 py-2.5 border ${
                        isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {row.label}
                      </div>
                      <div className={`text-[13px] font-semibold mt-0.5 ${isDark ? 'text-white' : 'text-[#0d3a71]'}`}>
                        {row.value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chips: Offline / New / Official */}
                {(infoTopic.offline_ready || infoTopic.is_new || infoTopic.is_official) && (
                  <div className="flex items-center flex-wrap gap-1.5">
                    {infoTopic.is_official && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
                        isDark ? 'bg-brand-500/15 text-brand-300 border-brand-400/40' : 'bg-brand-50 text-brand-600 border-brand-200'
                      }`}>Official</span>
                    )}
                    {infoTopic.offline_ready && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold border" style={{ background: 'rgba(34,211,238,0.15)', color: isDark ? '#a5f3fc' : '#0e7490', borderColor: 'rgba(34,211,238,0.45)' }}>Offline ready</span>
                    )}
                    {infoTopic.is_new && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold border" style={{ background: 'rgba(34,211,238,0.15)', color: isDark ? '#a5f3fc' : '#0e7490', borderColor: 'rgba(34,211,238,0.45)' }}>New</span>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    const t = infoTopic;
                    setInfoTopic(null);
                    if (t.status !== 'coming_soon') handleTopicSelect(t);
                  }}
                  disabled={infoTopic.status === 'coming_soon'}
                  className={`w-full inline-flex items-center justify-between h-10 rounded-[8px] px-4 text-[13px] font-semibold transition-all ${
                    infoTopic.status === 'coming_soon'
                      ? 'bg-gray-200 text-gray-500'
                      : 'bg-brand-500 text-white hover:bg-brand-600'
                  }`}
                  data-testid="topic-info-cta"
                >
                  <span>
                    {infoTopic.status === 'coming_soon'
                      ? getText('card_coming_soon', 'Coming Soon')
                      : (Math.round(infoTopic.mastery_percentage ?? infoTopic.progress ?? 0) > 0
                          ? getText('card_continue_btn', 'Continue')
                          : getText('card_start_now', 'Start Now'))}
                  </span>
                  {infoTopic.status !== 'coming_soon' && <ArrowRight className="w-4 h-4" strokeWidth={2.5} />}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game-mode Instructions Modal — opened from the `i` icon on each
          game card. Surfaces how to play each game. */}
      <AnimatePresence>
        {gameInfoMode && (() => {
          const { Icon } = gameInfoMode;
          return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
            onClick={() => setGameInfoMode(null)}
            data-testid="game-info-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className={`relative w-full max-w-md rounded-[12px] overflow-hidden border ${
                isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="p-5 flex items-center gap-4"
                style={{ background: '#1560bd' }}
              >
                <div className="w-12 h-12 rounded-[10px] flex items-center justify-center bg-white/20">
                  {Icon && <Icon className="w-7 h-7 text-white" strokeWidth={2} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80 mb-0.5">
                    How to play
                  </div>
                  <h3 className="text-[18px] font-semibold text-white leading-tight">
                    {gameInfoMode.label}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setGameInfoMode(null)}
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white/85 hover:bg-white/15 hover:text-white transition-colors"
                  aria-label="Close"
                  data-testid="game-info-close"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>

              <div className="p-5 space-y-4">
                <ol className="space-y-2.5 list-none">
                  {(gameInfoMode.instructions || []).map((step, i) => (
                    <li key={i} className={`flex items-start gap-3 text-[13px] leading-relaxed ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      <span
                        className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold text-white"
                        style={{ background: '#1560bd' }}
                        aria-hidden
                      >
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                {gameInfoMode.prize_rule && (
                  <div className={`rounded-[8px] p-3 border flex items-start gap-2 ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#fff7ed] border-[#fed7aa]'}`}>
                    <span className="text-[14px] leading-none" aria-hidden>🎁</span>
                    <div className={`text-[12px] leading-relaxed ${isDark ? 'text-amber-200' : 'text-[#9a3412]'}`}>
                      <strong className="font-semibold">How to win:</strong> {gameInfoMode.prize_rule}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const m = gameInfoMode;
                    try { localStorage.setItem(`game_instructions_seen_${m.id}`, '1'); } catch (_) { /* ignore */ }
                    setGameInfoMode(null);
                    try {
                      const saved = JSON.parse(localStorage.getItem(`game_prefs_${m.id}`) || 'null');
                      if (saved?.category && saved?.playType) {
                        navigate(`/games/${m.id}/pool?cat=${saved.category}&mode=${m.id}&play=${saved.playType}&count=10`);
                        return;
                      }
                    } catch (_) { /* ignore */ }
                    navigate(`/games?mode=${m.id}`);
                  }}
                  className="w-full inline-flex items-center justify-between h-10 rounded-[8px] px-4 text-[13px] font-semibold transition-all text-white"
                  style={{ background: '#1560bd' }}
                  data-testid="game-info-cta"
                >
                  <span>Start {gameInfoMode.label}</span>
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            </motion.div>
          </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
